import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CheckCircle, AlertCircle, AlertTriangle, Calendar, Download, RefreshCcw, ChevronDown, ChevronRight,
  ExternalLink, Loader2, Check, X, Printer, Eye, Building2, Hash, MapPin, Pencil, FileDown, Sparkles,
  Trash2, Send, Zap, Ban, Receipt, SlidersHorizontal, Undo2,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { format, parseISO, startOfMonth, subMonths } from 'date-fns';
import clsx from 'clsx';
import { isoDate } from '../../utils/time';
import { codeLabel, codeWords } from '../../utils/format';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PAGE_WIDE, Dialog, KpiTile, EmptyState, ErrorState, TableSkeleton, BulkBar, OverflowMenu, SearchInput,
  FilterChips, Tabs, useUrlTab, PersonAvatar, StatusPill, money, num, plural, fmtDate, fmtDayMonth,
} from '../../components/ui';
import { Notice } from '../Financials/financeKit';
import SilWeeklyTab from './SilWeeklyTab';
import XeroInvoicesTab from './XeroInvoicesTab';
import QuickBooksInvoicesTab from './QuickBooksInvoicesTab';
import useConnectedIntegrations from '../../hooks/useConnectedIntegrations';

const INVOICE_DEFAULTS = {
  abn: '', address: '', phone: '', payment_terms: '', contact_email: '',
  payment_rounding: 2, ndia_provider_number: '', cost_calculation_based_on: 'end_time',
  default_due_days: 14, invoice_approved_only: false, show_total_quantity: false,
  mileage_notional_pricing: false, invoice_item_format: '', fixed_recurring_format: '',
};

const InvSettingsCtx = createContext(INVOICE_DEFAULTS);
const useInvSettings = () => useContext(InvSettingsCtx);

const warnIcon = <AlertTriangle size={16} className="text-amber-500" />;

// Columns that only earn their space on very wide screens (the same values are one click away).
const WIDE_ONLY = 'hidden 2xl:table-cell';

// Header / cell classes for the hand-written tables on this page: white header, sentence case,
// numbers right-aligned in tabular figures.
const TH = 'px-3 py-2.5 text-left text-xs font-semibold text-slate-500 whitespace-nowrap';
const TH_NUM = 'px-3 py-2.5 text-right text-xs font-semibold text-slate-500 whitespace-nowrap';

// Money on an invoice honours the invoice rounding setting (dp). At 2 dp — the default — it is the
// app-wide money(); at any other setting the digits are exactly what toFixed(dp) gave before, now
// grouped, with the sign before the dollar.
function fmtDp(n, dp = 2) {
  const v = Number(n) || 0;
  if (dp === 2 || dp == null) return money(v);
  const fixed = Math.abs(v).toFixed(dp);
  const [i, f] = fixed.split('.');
  const grouped = Number(i).toLocaleString('en-AU');
  const zero = Number(fixed) === 0;
  return `${v < 0 && !zero ? '-' : ''}$${grouped}${f ? `.${f}` : ''}`;
}

// When the claim file is refused, the server sends back exactly which lines are wrong and why —
// but every export button used to throw that away and show only the one-line summary ("1 line(s)
// cannot be claimed yet"), leaving the operator to guess which line and what to do. This carries
// the detail to one shared panel so all four export buttons explain themselves the same way.
const ClaimErrCtx = createContext(() => {});
const useClaimErrors = () => useContext(ClaimErrCtx);

// Reads a failed export response. Returns true when it was a claim-validation refusal (and has
// been shown in the panel), false when it is an ordinary error for the caller to toast.
async function readClaimRefusal(resp, show) {
  const err = await resp.json().catch(() => ({}));
  if (err && err.code === 'CLAIM_FILE_INVALID' && Array.isArray(err.errors) && err.errors.length) {
    show({ message: err.message, errors: err.errors, validRows: err.valid_rows ?? 0 });
    return true;
  }
  return err;
}

function ClaimErrorPanel({ data, onClose }) {
  if (!data) return null;
  return (
    <Dialog open onClose={onClose} size="lg" title="These lines cannot be claimed yet"
      description={`Nothing was downloaded. Fix the points below and export again — a file the NDIA rejects costs a whole claim cycle.${data.validRows > 0 ? ` ${data.validRows} other line${data.validRows !== 1 ? 's are' : ' is'} ready.` : ''}`}
      footer={<button type="button" onClick={onClose} className="btn-secondary">Close</button>}>
      <div className="space-y-3">
        {data.errors.map((e, i) => (
          <div key={e.line_id || i} className="rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-900">
              {e.participant || 'Unknown participant'}
              {e.invoice_number && <span className="font-medium text-red-700"> · {e.invoice_number}</span>}
            </p>
            <ul className="mt-2 space-y-1.5">
              {(e.problems || []).map((prob, j) => (
                <li key={j} className="text-[13px] text-red-800 leading-relaxed flex gap-2">
                  <span className="text-red-500 shrink-0" aria-hidden="true">•</span><span>{prob}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Dialog>
  );
}

// ── Status config ────────────────────────────────────────────────────────────
// The statuses an invoice can be set to by hand (edit form). Display uses the app's StatusPill.
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'voided', label: 'Void' },
  { value: 'invoiced', label: 'Invoiced' },
  { value: 'pending', label: 'Pending' },
];
const PAY_TO_OPTS = [
  { value: 'client',       label: 'Client' },
  { value: 'ndia',         label: 'NDIA' },
  { value: 'plan_manager', label: 'Plan manager' },
  { value: 'self_managed', label: 'Self-managed' },
  { value: 'services_australia',     label: 'Services Australia' },
  { value: 'aged_care_contribution', label: 'Aged care contribution' },
];
// Older and seeded invoices store 'ndis' where newer ones store 'ndia'; both are the NDIA paying, so
// both read 'NDIA'. Any other stored code is written out in words rather than shown raw.
const payTypeLabel = (t) => ({ ndia: 'NDIA', ndis: 'NDIA', plan_manager: 'Plan manager', self_managed: 'Self-managed', self_funded: 'Self-funded', client: 'Client', aged_care: 'Services Australia', services_australia: 'Services Australia', aged_care_contribution: 'Aged care contribution' }[t] || codeLabel(t) || '—');

// Only an NDIA-managed invoice can be claimed through PRODA or the NDIA API. An aged care invoice is
// claimed from Services Australia, per period, on the Aged Care Claims screen.
const isNdiaPayer = (t) => t === 'ndia' || t === 'ndis';
// The Services Australia invoice is the Support at Home claim for the visits, less the
// participant's contribution. It is claimed, not emailed. 'aged_care' is its earlier name.
const isSaPayer = (t) => t === 'services_australia' || t === 'aged_care';
const AGED_CARE_CLAIMS = '/aged-care?tab=claims';

// Default the invoice recipient from the client's funding-management type so
// admins don't have to set "TO" by hand on every row. Falls back to Client.
// An aged care participant is never NDIS-funded: their invoices are aged care, and the server
// enforces the same rule so one can never reach the NDIA claim file. A CHSP participant pays their
// own contribution: the server puts CHSP visits on an invoice to them whatever is chosen here.
function payToFromClientType(clientType, serviceType, program) {
  if (program === 'chsp') return 'client';
  if (serviceType === 'aged_care') return 'services_australia';
  switch (clientType) {
    case 'self_managed':  return 'self_managed';
    case 'plan_managed':  return 'plan_manager';
    case 'ndia_managed':
    case 'ndis_managed':
    case 'agency_managed': return 'ndia';
    default: return 'client';
  }
}

// ── Modal wrapper ────────────────────────────────────────────────────────────
// The app's Dialog (focus kept inside, Escape and backdrop close, labelled). Children bring their
// own padded sections and footers.
function Modal({ title, onClose, children, wide, dismissable = true }) {
  return (
    <Dialog open onClose={onClose} title={title} size={wide ? 'xl' : 'lg'} dismissable={dismissable}
      bodyClassName="!px-0 sm:!px-0 !pb-0">
      {children}
    </Dialog>
  );
}

// ── Shift Report Modal (for "View report" in Generate tab) ───────────────────
function ShiftReportModal({ client, dateRange, onClose }) {
  return (
    <Modal title={`Shift report · ${client.clientName}`} onClose={onClose} wide>
      <div className="px-6 py-2 space-y-4">
        {/* Header info */}
        <div className="flex flex-wrap gap-4 text-sm text-slate-600">
          <span className="flex items-center gap-1.5"><Calendar size={13} className="text-slate-500" />
            {fmtDate(dateRange.start)} – {fmtDate(dateRange.end)}
          </span>
          {client.ndisNumber && (
            <span className="flex items-center gap-1.5"><Hash size={13} className="text-slate-500" />
              NDIS {client.ndisNumber}
            </span>
          )}
        </div>

        {/* Shifts table */}
        <div className="border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white border-b border-slate-200">
                <th scope="col" className={TH}>Date</th>
                <th scope="col" className={TH}>Staff</th>
                <th scope="col" className={TH_NUM}>Hours</th>
                <th scope="col" className={TH_NUM}>Rate/hr</th>
                <th scope="col" className={TH_NUM}>Amount</th>
                <th scope="col" className={TH}>Timesheet</th>
              </tr>
            </thead>
            <tbody>
              {client.shifts.map((s, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0">
                  <td className="px-3 py-2.5 font-medium text-slate-800 whitespace-nowrap">
                    {s.date ? format(parseISO(String(s.date).slice(0, 10)), 'EEE d MMM yyyy') : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-slate-700">{s.staffName || '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-800">{parseFloat(s.hours).toFixed(2)} h</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-800">{money(s.rate)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-900">{money(s.lineTotal)}</td>
                  <td className="px-3 py-2.5">
                    {s.tsStatus ? <StatusPill status={s.tsStatus} /> : <span className="badge-gray whitespace-nowrap">{s.cancelled ? 'Cancellation charge' : 'No timesheet'}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 border-t border-slate-300 font-semibold">
                <td colSpan={2} className="px-3 py-2.5 text-sm text-slate-800">
                  Total ({plural(client.shifts.length, 'shift')})
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-900">{parseFloat(client.totalHours).toFixed(2)} h</td>
                <td />
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-900">{money(client.totalCost)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Mileage/expense summary */}
        {(client.totalMileage > 0 || client.totalExpense > 0) && (
          <div className="flex flex-wrap gap-3 text-sm text-slate-700">
            {client.totalMileage > 0 && (
              <div className="flex items-center gap-1.5 bg-slate-50 rounded-lg px-3 py-2">
                <MapPin size={13} className="text-slate-500" />
                Mileage: <strong className="tabular-nums">{num(client.totalMileage, 1)} km</strong>
                <span className="text-slate-500 text-xs">(included in the total)</span>
              </div>
            )}
            {client.totalExpense > 0 && (
              <div className="flex items-center gap-1.5 bg-slate-50 rounded-lg px-3 py-2">
                Expenses: <strong className="tabular-nums">{money(client.totalExpense)}</strong>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="px-6 py-4 mt-4 border-t border-slate-100 flex justify-end">
        <button type="button" onClick={onClose} className="btn-secondary">Close</button>
      </div>
    </Modal>
  );
}

// ── Invoice Detail Modal ──────────────────────────────────────────────────────
function InvoiceDetailModal({ invoiceId, onClose, onSaved }) {
  const invSettings = useInvSettings();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const printRef = useRef();
  const dp = invSettings.payment_rounding ?? 2;

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/invoices/${invoiceId}`)
      .then(r => setInvoice(r.data))
      .catch((e) => toast.error(e.response?.data?.message || 'Failed to load invoice'))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    setEditData({
      status: invoice.status,
      due_date: invoice.due_date?.slice(0, 10) || '',
      payment_type: invoice.payment_type || 'ndia',
      notes: invoice.notes || '',
      purchase_order: invoice.purchase_order || '',
      include_tax: invoice.include_tax || false,
      lineItems: (invoice.lineItems || []).map(li => ({
        shift_id: li.shift_id || null,
        description: li.description || '',
        support_item_number: li.support_item_number || '',
        hours_units: parseFloat(li.hours_units || 0),
        rate: parseFloat(li.rate || 0),
        item_date: li.item_date?.slice(0, 10) || '',
      })),
    });
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setEditData(null); };

  const updateLI = (idx, field, val) => {
    setEditData(d => {
      const li = [...d.lineItems];
      li[idx] = { ...li[idx], [field]: val };
      return { ...d, lineItems: li };
    });
  };

  const addLI = () => {
    setEditData(d => ({
      ...d,
      lineItems: [...d.lineItems, { description: '', support_item_number: '', hours_units: 0, rate: 0, item_date: '' }],
    }));
  };

  const removeLI = idx => setEditData(d => ({ ...d, lineItems: d.lineItems.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/invoices/${invoiceId}`, editData);
      toast.success('Invoice updated');
      load();
      setEditing(false);
      setEditData(null);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save invoice'); }
    finally { setSaving(false); }
  };

  const handleClone = async () => {
    try {
      await api.post('/invoices', {
        clientId: invoice.client_id,
        invoiceDate: isoDate(),
        dueDate: invoice.due_date?.slice(0, 10) || null,
        paymentType: invoice.payment_type,
        notes: invoice.notes ? `Cloned from ${invoice.invoice_number}` : null,
        purchaseOrder: invoice.purchase_order || null,
        includeTax: invoice.include_tax || false,
        lineItems: (invoice.lineItems || []).map(li => ({
          description: li.description,
          supportItemNumber: li.support_item_number,
          hoursUnits: li.hours_units,
          rate: li.rate,
          itemDate: li.item_date?.slice(0, 10) || null,
        })),
      });
      toast.success('Invoice cloned as a new draft');
      onClose();
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to clone invoice'); }
  };

  const [sending, setSending] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const handleSend = async () => {
    setSending(true);
    try { const { data } = await api.post(`/invoices/${invoiceId}/send`); toast.success(`Invoice emailed to ${data.to}`); setInvoice(i => ({ ...i, status: i.status === 'draft' ? 'sent' : i.status, emailed_at: new Date().toISOString() })); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed to send'); } finally { setSending(false); }
  };

  const handlePrint = () => {
    const content = printRef.current?.innerHTML;
    if (!content) return;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Invoice ${invoice?.invoice_number}</title>
      <style>
        body { font-family: system-ui, sans-serif; padding: 40px; color: #1e293b; }
        table { width: 100%; border-collapse: collapse; margin: 16px 0; }
        th { background: #f8fafc; text-align: left; padding: 8px 12px; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; color: #64748b; border-bottom: 2px solid #e2e8f0; }
        td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; font-size: 13px; }
        tfoot td { background: #f8fafc; font-weight: 600; border-top: 2px solid #e2e8f0; }
        .header { display: flex; justify-content: space-between; margin-bottom: 32px; }
        .badge { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; background: #f1f5f9; color: #475569; }
        .total-row { font-size: 16px; }
        h1 { font-size: 24px; margin: 0 0 4px; } h3 { margin: 0 0 8px; font-size: 14px; }
        p { margin: 2px 0; font-size: 13px; color: #64748b; }
        @media print { body { padding: 20px; } }
      </style></head><body>${content}</body></html>
    `);
    win.document.close(); win.focus(); win.print(); win.close();
  };

  if (loading) return (
    <Modal title="Invoice" onClose={onClose} wide>
      <div className="px-6 pb-6"><TableSkeleton rows={5} cols={4} /></div>
    </Modal>
  );
  if (!invoice) return null;

  const inputCls = 'border border-slate-200 rounded-lg px-2 py-1.5 text-xs w-full focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-400';

  // ── EDIT MODE ─────────────────────────────────────────────────────────────
  if (editing && editData) {
    const editSubtotal = editData.lineItems.reduce((s, li) =>
      s + (parseFloat(li.hours_units) || 0) * (parseFloat(li.rate) || 0), 0);
    const editGst = editData.include_tax ? editSubtotal * 0.1 : 0;
    return (
      <Modal title={`Edit invoice ${invoice.invoice_number}`} onClose={onClose} wide>
        <div className="px-6 py-2 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="inv-edit-status" className="text-[13px] text-slate-700 font-medium block mb-1">Status</label>
              <select id="inv-edit-status" value={editData.status} onChange={e => setEditData(d => ({ ...d, status: e.target.value }))} className="input-field">
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="inv-edit-due" className="text-[13px] text-slate-700 font-medium block mb-1">Due date</label>
              <input id="inv-edit-due" type="date" value={editData.due_date} onChange={e => setEditData(d => ({ ...d, due_date: e.target.value }))} className="input-field" />
            </div>
            <div>
              <label htmlFor="inv-edit-pay" className="text-[13px] text-slate-700 font-medium block mb-1">Payment type</label>
              <select id="inv-edit-pay" value={editData.payment_type} onChange={e => setEditData(d => ({ ...d, payment_type: e.target.value }))} className="input-field">
                {PAY_TO_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="inv-edit-po" className="text-[13px] text-slate-700 font-medium block mb-1">Purchase order</label>
              <input id="inv-edit-po" type="text" value={editData.purchase_order} onChange={e => setEditData(d => ({ ...d, purchase_order: e.target.value }))} className="input-field" placeholder="PO number" />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="inv-edit-notes" className="text-[13px] text-slate-700 font-medium block mb-1">Notes</label>
              <textarea id="inv-edit-notes" value={editData.notes} onChange={e => setEditData(d => ({ ...d, notes: e.target.value }))} className="input-field resize-none" rows={2} placeholder="Invoice notes…" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="edit-gst" checked={editData.include_tax} onChange={e => setEditData(d => ({ ...d, include_tax: e.target.checked }))} className="rounded" />
              <label htmlFor="edit-gst" className="text-sm text-slate-700">Include GST (10%)</label>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-800">Line items</h3>
              <button type="button" onClick={addLI} className="btn-ghost btn-sm">+ Add line</button>
            </div>
            <div className="border border-slate-200 rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-white border-b border-slate-200">
                    <th scope="col" className={clsx(TH, 'w-32')}>Date</th>
                    <th scope="col" className={TH}>Description</th>
                    <th scope="col" className={clsx(TH, 'w-36')}>Support item</th>
                    <th scope="col" className={clsx(TH_NUM, 'w-20')}>Hours</th>
                    <th scope="col" className={clsx(TH_NUM, 'w-24')}>Rate</th>
                    <th scope="col" className={clsx(TH_NUM, 'w-28')}>Total</th>
                    <th scope="col" className="w-10"><span className="sr-only">Remove</span></th>
                  </tr>
                </thead>
                <tbody>
                  {editData.lineItems.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-6 text-slate-500 text-sm">No line items. Select Add line to add one.</td></tr>
                  )}
                  {editData.lineItems.map((li, i) => {
                    const lt = (parseFloat(li.hours_units) || 0) * (parseFloat(li.rate) || 0);
                    const n = i + 1;
                    return (
                      <tr key={i} className="border-b border-slate-100 last:border-0">
                        <td className="px-2 py-2">
                          <input type="date" value={li.item_date} onChange={e => updateLI(i, 'item_date', e.target.value)}
                            aria-label={`Line ${n} date`} className={inputCls} />
                        </td>
                        <td className="px-2 py-2">
                          <input type="text" value={li.description} onChange={e => updateLI(i, 'description', e.target.value)}
                            aria-label={`Line ${n} description`} className={inputCls} placeholder="Description" />
                        </td>
                        <td className="px-2 py-2">
                          <input type="text" value={li.support_item_number} onChange={e => updateLI(i, 'support_item_number', e.target.value)}
                            aria-label={`Line ${n} support item number`} className={clsx(inputCls, 'font-mono')} placeholder="01_011_0107_1_1" />
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" value={li.hours_units} onChange={e => updateLI(i, 'hours_units', e.target.value)}
                            aria-label={`Line ${n} hours`} min="0" step="0.25" className={clsx(inputCls, 'text-right tabular-nums')} />
                        </td>
                        <td className="px-2 py-2">
                          <input type="number" value={li.rate} onChange={e => updateLI(i, 'rate', e.target.value)}
                            aria-label={`Line ${n} rate`} min="0" step="0.01" className={clsx(inputCls, 'text-right tabular-nums')} />
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-900 text-sm tabular-nums whitespace-nowrap">{money(lt)}</td>
                        <td className="px-2 py-2">
                          <button type="button" onClick={() => removeLI(i)} aria-label={`Remove line ${n}`}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50"><X size={14} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {editGst > 0 && (
                    <tr className="bg-slate-50 border-t border-slate-200">
                      <td colSpan={5} className="px-3 py-2 text-right text-xs font-medium text-slate-600">GST (10%)</td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-900 tabular-nums whitespace-nowrap">{fmtDp(editGst, dp)}</td>
                      <td />
                    </tr>
                  )}
                  <tr className="bg-slate-50 border-t border-slate-300">
                    <td colSpan={5} className="px-3 py-2.5 text-right font-bold text-slate-800">Total</td>
                    <td className="px-3 py-2.5 text-right font-bold text-slate-900 text-base tabular-nums whitespace-nowrap">{fmtDp(editSubtotal + editGst, dp)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 mt-5 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50/60">
          <button type="button" onClick={cancelEdit} className="btn-secondary">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving} className="btn-primary">
            {saving && <Loader2 size={13} className="animate-spin" />}
            Save changes
          </button>
        </div>
      </Modal>
    );
  }

  // ── VIEW MODE ─────────────────────────────────────────────────────────────
  const subtotal = (invoice.lineItems || []).reduce((sum, li) => sum + parseFloat(li.line_total || 0), 0);
  const gst = invoice.include_tax ? subtotal * 0.1 : 0;
  const total = subtotal + gst;

  return (
    <Modal title={`Invoice ${invoice.invoice_number}`} onClose={onClose} wide dismissable={!payOpen}>
      <div ref={printRef} className="px-6 py-2 space-y-6">
        {/* Invoice header */}
        <div className="flex flex-wrap justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold text-slate-900">Invoice</h1>
              <StatusPill status={invoice.status} />
            </div>
            <p className="text-sm font-mono text-slate-600">{invoice.invoice_number}</p>
          </div>
          <div className="text-right text-sm text-slate-700 space-y-1">
            <p><span className="text-slate-500">Date:</span> <strong>{invoice.invoice_date ? fmtDate(invoice.invoice_date) : '—'}</strong></p>
            {invoice.due_date && (
              <p><span className="text-slate-500">Due:</span> <strong>{fmtDate(invoice.due_date)}</strong></p>
            )}
            {invoice.purchase_order && (
              <p><span className="text-slate-500">PO:</span> <strong>{invoice.purchase_order}</strong></p>
            )}
          </div>
        </div>

        {/* Client info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-500 mb-2">Bill to</p>
            <div className="flex items-center gap-2">
              <PersonAvatar id={invoice.client_id} name={invoice.client_name} size="md" />
              <div>
                <p className="font-semibold text-slate-900">{invoice.client_name}</p>
                {invoice.ndis_number && <p className="text-xs text-slate-600">NDIS {invoice.ndis_number}</p>}
                {invoice.client_address && <p className="text-xs text-slate-600">{invoice.client_address}</p>}
              </div>
            </div>
          </div>
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-500 mb-2">From</p>
            <div className="flex items-center gap-2">
              <span className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0">
                <Building2 size={16} className="text-slate-500" />
              </span>
              <div>
                <p className="font-semibold text-slate-900">{invSettings.company_name || 'Your company'}</p>
                {invSettings.abn && <p className="text-xs text-slate-600">ABN {invSettings.abn}</p>}
                {invSettings.ndia_provider_number && <p className="text-xs text-slate-600">NDIA provider {invSettings.ndia_provider_number}</p>}
                {invSettings.address && <p className="text-xs text-slate-600">{invSettings.address}</p>}
                {invSettings.phone && <p className="text-xs text-slate-600">{invSettings.phone}</p>}
                {invSettings.contact_email && <p className="text-xs text-slate-600">{invSettings.contact_email}</p>}
                {invSettings.payment_terms && <p className="text-xs text-slate-600 mt-1">Terms: {invSettings.payment_terms}</p>}
                <p className="text-xs text-slate-600">
                  {(invoice.payment_type === 'ndia' || invoice.payment_type === 'ndis') ? 'NDIA funded' :
                   invoice.payment_type === 'plan_manager' ? 'Plan-managed' :
                   invoice.payment_type === 'self_managed' ? 'Self-managed' :
                   isSaPayer(invoice.payment_type) ? 'Support at Home claim to Services Australia' :
                   invoice.payment_type === 'aged_care_contribution' ? 'Support at Home: your contribution' : 'Private'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {invoice.notes && <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 px-4 py-2.5 rounded-xl">{invoice.notes}</p>}
        {invSettings.email_message && <p className="text-xs text-slate-600 bg-slate-50 px-4 py-2.5 rounded-xl italic">{invSettings.email_message}</p>}

        {/* Line items */}
        <div className="border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white border-b border-slate-200">
                <th scope="col" className={TH}>Date</th>
                <th scope="col" className={TH}>Description</th>
                <th scope="col" className={TH}>Support item</th>
                <th scope="col" className={TH_NUM}>Hours</th>
                <th scope="col" className={TH_NUM}>Rate</th>
                <th scope="col" className={TH_NUM}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {(invoice.lineItems || []).length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-slate-500">No line items</td></tr>
              )}
              {(invoice.lineItems || []).map((li, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0">
                  <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">
                    {li.item_date ? format(parseISO(li.item_date.slice(0, 10)), 'EEE d MMM yyyy') : '—'}
                  </td>
                  <td className="px-3 py-2.5 font-medium text-slate-900">{codeWords(li.description) || 'Support service'}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{li.support_item_number || '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-800">{parseFloat(li.hours_units || 0).toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-800 whitespace-nowrap">{money(li.rate)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-900 whitespace-nowrap">{money(li.line_total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200 bg-slate-50">
                <td colSpan={5} className="px-3 py-2.5 text-right text-sm font-medium text-slate-600">Subtotal</td>
                <td className="px-3 py-2.5 text-right font-semibold text-slate-900 tabular-nums whitespace-nowrap">{fmtDp(subtotal, dp)}</td>
              </tr>
              {gst > 0 && (
                <tr className="bg-slate-50">
                  <td colSpan={5} className="px-3 py-2.5 text-right text-sm font-medium text-slate-600">GST (10%)</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-slate-900 tabular-nums whitespace-nowrap">{fmtDp(gst, dp)}</td>
                </tr>
              )}
              {invSettings.show_total_quantity && (
                <tr className="bg-slate-50">
                  <td colSpan={5} className="px-3 py-2.5 text-right text-sm font-medium text-slate-600">Total hours</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-slate-900 tabular-nums">
                    {(invoice.lineItems || []).reduce((s, li) => s + parseFloat(li.hours_units || 0), 0).toFixed(2)} h
                  </td>
                </tr>
              )}
              <tr className="bg-slate-50 border-t border-slate-300">
                <td colSpan={5} className="px-3 py-3 text-right font-bold text-slate-900 text-base">Total</td>
                <td className="px-3 py-3 text-right font-bold text-slate-900 text-lg tabular-nums whitespace-nowrap">{fmtDp(total, dp)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Actions footer */}
      <div className="px-6 py-4 mt-6 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap bg-slate-50/60">
        <p className="text-xs text-slate-600">
          {plural(invoice.lineItems?.length || 0, 'line item')}
        </p>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          <button type="button" onClick={() => setPayOpen(true)} className="btn-ghost text-sm"><Check size={14} /> Record payment</button>
          {!isSaPayer(invoice.payment_type) && (
            <button type="button" onClick={handleSend} disabled={sending} className="btn-ghost text-sm"><Send size={14} /> {sending ? 'Sending…' : 'Send by email'}</button>
          )}
          <button type="button" onClick={handlePrint} className="btn-ghost text-sm"><Printer size={14} /> Print or PDF</button>
          <button type="button" onClick={handleClone} className="btn-ghost text-sm"><RefreshCcw size={14} /> Clone as draft</button>
          <button type="button" onClick={startEdit} className="btn-ghost text-sm"><Pencil size={14} /> Edit</button>
          <button type="button" onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </div>
      {payOpen && <RecordPaymentModal invoice={invoice} onClose={() => setPayOpen(false)} onSaved={(u) => { setInvoice(i => ({ ...i, ...u })); setPayOpen(false); onSaved?.(); }} />}
    </Modal>
  );
}

// Record a (partial) payment + reference (Brevity §5.6).
function RecordPaymentModal({ invoice, onClose, onSaved }) {
  const total = Number(invoice.total_amount) || 0;
  const [amountPaid, setAmountPaid] = useState(invoice.amount_paid != null ? String(invoice.amount_paid) : String(total));
  const [paymentReference, setPaymentReference] = useState(invoice.payment_reference || '');
  const [paidDate, setPaidDate] = useState((invoice.paid_date || new Date().toISOString()).slice(0, 10));
  const [saving, setSaving] = useState(false);
  const paid = Number(amountPaid) || 0;
  const status = paid >= total && total > 0 ? 'paid' : paid > 0 ? 'sent' : invoice.status;
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put(`/invoices/${invoice.id}/payment`, { amountPaid, paymentReference, paidDate, status }); toast.success('Payment recorded'); onSaved(data); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <Dialog open onClose={onClose} size="sm" title="Record payment"
      description={`Invoice ${invoice.invoice_number} · total ${money(total)}`}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Record payment'}</button>
        </>
      )}>
      <div className="space-y-3">
        <div>
          <label htmlFor="pay-amount" className="block text-[13px] font-medium text-slate-700 mb-1">Amount paid ($)</label>
          <input id="pay-amount" type="number" step="0.01" className="input-field tabular-nums" value={amountPaid} onChange={e => setAmountPaid(e.target.value)} />
        </div>
        <div>
          <label htmlFor="pay-ref" className="block text-[13px] font-medium text-slate-700 mb-1">Payment reference</label>
          <input id="pay-ref" className="input-field" value={paymentReference} onChange={e => setPaymentReference(e.target.value)} placeholder="Bank or EFT reference" />
        </div>
        <div>
          <label htmlFor="pay-date" className="block text-[13px] font-medium text-slate-700 mb-1">Paid date</label>
          <input id="pay-date" type="date" className="input-field" value={paidDate} onChange={e => setPaidDate(e.target.value)} />
        </div>
        <p className="text-xs text-slate-600">
          Status will be set to <b>{status}</b>{paid > 0 && paid < total ? ` (part-paid, ${money(total - paid)} outstanding)` : ''}.
        </p>
      </div>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GENERATE TAB
// ─────────────────────────────────────────────────────────────────────────────

// A preview row is ready when it has something the invoice will bill: an approved shift, or a
// cancellation charge (billable with no timesheet).
const isReady = (c) => (c.approvedShifts || 0) > 0 || (c.shifts || []).some(s => s.cancelled);

// Per-row invoice options (PO, non-shift PO, GST) for screens where those columns are hidden.
function InvoiceOptionsDialog({ client, rd, onChange, onClose }) {
  return (
    <Dialog open onClose={onClose} size="sm" title="Invoice options" description={client.clientName}
      footer={<button type="button" onClick={onClose} className="btn-primary">Done</button>}>
      <div className="space-y-3">
        <div>
          <label htmlFor="opt-po" className="block text-[13px] font-medium text-slate-700 mb-1">Purchase order</label>
          <input id="opt-po" type="text" className="input-field" placeholder="PO number" value={rd.purchaseOrder || ''}
            onChange={e => onChange('purchaseOrder', e.target.value)} />
        </div>
        <div>
          <label htmlFor="opt-nspo" className="block text-[13px] font-medium text-slate-700 mb-1">Non-shift purchase order</label>
          <input id="opt-nspo" type="text" className="input-field" placeholder="PO for non-shift lines" value={rd.nonShiftPO || ''}
            onChange={e => onChange('nonShiftPO', e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" className="rounded" checked={rd.includeTax || false} onChange={e => onChange('includeTax', e.target.checked)} />
          Add GST (10%) to this invoice
        </label>
      </div>
    </Dialog>
  );
}

function GenerateTab() {
  const confirm = useConfirm();
  const showClaimErrors = useClaimErrors();
  const invSettings = useInvSettings();
  // Default to current week (Mon-Sun)
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const dow = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((dow + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = isoDate;
    return { start: fmt(monday), end: fmt(sunday) };
  });
  const [groupBy, setGroupBy]           = useState('client');
  const [costTypes, setCostTypes]       = useState({ hours: true, mileage: true, expenses: true, tasks: true });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advanced, setAdvanced] = useState({
    includeZeroHour: true,
    includeCancelled: false,
    autoGst: true,
    groupBySupportItem: false,
  });
  const [preview, setPreview]           = useState([]);
  const [loadingPreview, setLP]         = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [selected, setSelected]         = useState([]);
  const [rowData, setRowData]           = useState({});
  const [generating, setGenerating]     = useState(false);
  const [generated, setGenerated]       = useState([]);
  const [reportModal, setReportModal]   = useState(null); // client object
  const [optionsFor, setOptionsFor]     = useState(null); // client object
  const [viewInvoiceId, setViewInvoice] = useState(null);
  const [previewSearch, setPreviewSearch] = useState('');

  const defaultDueDays = invSettings.default_due_days ?? 14;
  const getDefaultDueDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + defaultDueDays);
    return isoDate(d);
  };

  const loadPreview = useCallback(() => {
    if (!dateRange.start || !dateRange.end) return;
    setLP(true);
    setPreviewError(null);
    const p = new URLSearchParams({
      startDate: dateRange.start, endDate: dateRange.end,
      groupBy,
      includeMileage: costTypes.mileage ? 'true' : 'false',
      includeExpenses: costTypes.expenses ? 'true' : 'false',
      includeCancelled: advanced.includeCancelled ? 'true' : 'false',
      includeZeroHour: advanced.includeZeroHour ? 'true' : 'false',
      groupBySupportItem: advanced.groupBySupportItem ? 'true' : 'false',
    });
    api.get(`/invoices/generate?${p}`)
      .then(r => {
        setPreview(r.data);
        const rd = {};
        r.data.forEach(c => { rd[c.clientId] = { payTo: payToFromClientType(c.clientType, c.serviceType, c.program), purchaseOrder: '', nonShiftPO: '', dueDate: getDefaultDueDate(), includeTax: false }; });
        setRowData(rd);
        setSelected([]);
      })
      .catch((e) => setPreviewError(e.response?.data?.message || "We couldn't load the billing preview. Check your connection, then try again."))
      .finally(() => setLP(false));
  }, [dateRange, costTypes, groupBy, advanced]);

  useEffect(() => { loadPreview(); }, []);

  const toggleSelect = id => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const updateRow = (cid, field, val) => setRowData(rd => ({ ...rd, [cid]: { ...rd[cid], [field]: val } }));

  // What Generate will send: the ticked rows, or — with nothing ticked — every row that is ready.
  // A client with no approved shifts is only invoiced when someone ticks it on purpose.
  const selectedRows = preview.filter(c => selected.includes(c.clientId));
  const targetRows = selected.length > 0 ? selectedRows : preview.filter(isReady);
  const targetTotal = targetRows.reduce((s, c) => s + (c.totalCost || 0), 0);
  const rangeText = `${fmtDayMonth(dateRange.start)} – ${fmtDate(dateRange.end)}`;

  const handleGenerate = async () => {
    const targets = targetRows.map(c => c.clientId);
    if (!targets.length) { toast.error('No clients are ready to invoice'); return; }
    const n = targets.length;
    const names = targetRows.slice(0, 5).map(c => c.clientName).join(', ') + (n > 5 ? ` and ${n - 5} more` : '');
    const withTax = targetRows.filter(c => rowData[c.clientId]?.includeTax).length;
    const notReady = targetRows.filter(c => !isReady(c)).length;
    const skipped = selected.length > 0 ? 0 : preview.length - targetRows.length;
    const lines = [
      `For ${names}. Each invoice bills the shifts shown for that client, and those shifts are marked invoiced.`,
    ];
    if (withTax) lines.push(`GST is added to ${plural(withTax, 'invoice')} with Tax ticked.`);
    if (!costTypes.mileage || !costTypes.expenses) {
      lines.push('Mileage and expenses are billed on the invoice even when they are hidden from this preview, so the invoiced total can be higher.');
    }
    if (skipped) lines.push(`${plural(skipped, 'client')} with no approved shifts ${skipped === 1 ? 'is' : 'are'} skipped.`);
    if (notReady) lines.push(`${plural(notReady, 'ticked client')} ${notReady === 1 ? 'has' : 'have'} no approved shifts. Only what your invoice settings allow is billed, and a client with nothing billable is skipped.`);
    const ok = await confirm(lines.join('\n\n'), {
      title: `Create ${plural(n, 'invoice')} for ${rangeText} totalling ${money(targetTotal)}${withTax ? ' before GST' : ''}?`,
      confirmLabel: `Create ${plural(n, 'invoice')}`,
    });
    if (!ok) return;
    setGenerating(true);
    try {
      const items = targets.map(cid => ({ clientId: cid, ...rowData[cid] }));
      const r = await api.post('/invoices/generate', {
        items, startDate: dateRange.start, endDate: dateRange.end,
        includeCancelled: advanced.includeCancelled,
        includeZeroHour: advanced.includeZeroHour,
      });
      const created = Number(r.data?.created) || 0;
      if (created < n) {
        toast(`${plural(created, 'invoice')} created. ${plural(n - created, 'client')} had nothing billable and ${n - created === 1 ? 'was' : 'were'} skipped.`, { icon: warnIcon, duration: 8000 });
      } else {
        toast.success(`${plural(created, 'invoice')} created`);
      }
      setGenerated(r.data.invoices || []);
      setSelected([]);
      loadPreview();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to generate invoices');
    } finally {
      setGenerating(false);
    }
  };

  const downloadProdaForInvoices = async (invoiceIds) => {
    try {
      const params = new URLSearchParams({ invoice_ids: invoiceIds.join(',') });
      const resp = await fetch(`/api/invoices/proda-export?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!resp.ok) {
        // A claim-validation refusal carries per-line detail; show that instead of a bare summary.
        const err = await readClaimRefusal(resp, showClaimErrors);
        if (err === true) return;
        toast.error(err.message || 'No NDIS line items found on these invoices');
        return;
      }
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `PRODA_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('PRODA CSV downloaded. Upload it to the NDIA PRODA portal.');
    } catch { toast.error('PRODA export failed'); }
  };

  const [ndiaSubmitting, setNdiaSubmitting] = useState(false);
  const submitNdiaForInvoices = async (invoiceIds) => {
    const ok = await confirm('Their NDIA lines are sent to the NDIA as payment requests.', {
      title: `Submit ${plural(invoiceIds.length, 'invoice')} to the NDIA?`, confirmLabel: 'Submit to NDIA',
    });
    if (!ok) return;
    setNdiaSubmitting(true);
    try {
      const r = await api.post('/integrations/ndia/submit-claims', { invoiceIds });
      toast.success(`${r.data.submitted} claim${r.data.submitted !== 1 ? 's' : ''} submitted to NDIA`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'NDIA submission failed — check NDIA credentials in Settings');
    } finally { setNdiaSubmitting(false); }
  };

  // ── Derived money-management summary ──────────────────────────────────────
  const readyClients   = preview.filter(isReady);
  const readyAmount    = readyClients.reduce((s, c) => s + (c.totalCost || 0), 0);
  const totalBillable  = preview.reduce((s, c) => s + (c.totalCost || 0), 0);
  const totalShiftsInRange = preview.reduce((s, c) => s + (c.totalShifts || 0), 0);
  const needsApproval  = preview.filter(c => !isReady(c) && c.totalShifts > 0).length;

  const filteredPreview = previewSearch
    ? preview.filter(c => (c.clientName || '').toLowerCase().includes(previewSearch.toLowerCase()))
    : preview;

  const generateLabel = generating ? 'Creating…'
    : targetRows.length ? `Generate ${plural(targetRows.length, 'invoice')} · ${money(targetTotal)}` : 'Generate 0 invoices';

  const labelCls = 'text-xs font-medium text-slate-600';

  return (
    <div className="space-y-4">
      {/* Money-management summary — the range these cover is the Shift date below */}
      {preview.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KpiTile label="Ready to invoice" accent="primary" value={money(readyAmount)}
            sub={`${plural(readyClients.length, 'client')} · of ${money(totalBillable)} in range`} />
          <KpiTile label="Needs approval" accent="amber" value={num(needsApproval)}
            sub={needsApproval
              ? <Link to="/timesheets?status=pending" className="font-medium text-primary-700 hover:underline">Review timesheets</Link>
              : 'Every client in range has approved shifts'} />
          <KpiTile label="Shifts in range" accent="slate" value={num(totalShiftsInRange)} sub={rangeText} />
        </div>
      )}

      {/* Success banner with View links + instant claim actions */}
      {generated.length > 0 && (
        <div className="card p-4 border-emerald-200">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-emerald-800">
              <CheckCircle size={16} />
              <span className="font-semibold">{plural(generated.length, 'invoice')} created</span>
            </div>
            <button type="button" onClick={() => setGenerated([])} aria-label="Dismiss" className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"><X size={14} /></button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {generated.map(inv => (
              <button key={inv.id} type="button" onClick={() => setViewInvoice(inv.id)} className="btn-secondary btn-sm">
                <Eye size={12} /> {inv.invoice_number}
              </button>
            ))}
          </div>
          {(generated.some(i => isNdiaPayer(i.payment_type)) || generated.some(i => isSaPayer(i.payment_type))) && (
            <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-slate-100">
              <span className="text-xs text-slate-600 font-medium mr-1">Claim now:</span>
              {generated.some(i => isNdiaPayer(i.payment_type)) && (<>
                <button type="button" onClick={() => downloadProdaForInvoices(generated.filter(i => isNdiaPayer(i.payment_type)).map(i => i.id))} className="btn-secondary btn-sm">
                  <Download size={12} /> Download PRODA CSV
                </button>
                <button type="button" onClick={() => submitNdiaForInvoices(generated.filter(i => isNdiaPayer(i.payment_type)).map(i => i.id))} disabled={ndiaSubmitting} className="btn-secondary btn-sm">
                  {ndiaSubmitting ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                  Submit to NDIA
                </button>
              </>)}
              {generated.some(i => isSaPayer(i.payment_type)) && (
                <Link to={AGED_CARE_CLAIMS} className="btn-secondary btn-sm">
                  <ExternalLink size={12} /> Claim aged care from Services Australia
                </Link>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="card p-4 space-y-4">
        <div className="flex flex-wrap items-end gap-5">
          {/* Group by */}
          <div className="flex flex-col gap-1">
            <label htmlFor="gen-group" className={labelCls}>Group by</label>
            <select id="gen-group" value={groupBy} onChange={e => setGroupBy(e.target.value)}
              className="input-field !w-auto !py-2 text-sm min-w-[130px]">
              <option value="client">Client</option>
              <option value="staff">Staff</option>
              <option value="team">Team</option>
            </select>
          </div>

          {/* Cost checkboxes */}
          <div className="flex flex-col gap-1">
            <span className={labelCls} id="gen-cost-label">Include</span>
            <div className="flex items-center gap-1.5 flex-wrap" role="group" aria-labelledby="gen-cost-label">
              {[['hours','Hours'],['mileage','Mileage'],['expenses','Expenses'],['tasks','Tasks']].map(([k,l]) => (
                <button key={k} type="button" aria-pressed={!!costTypes[k]} onClick={() => setCostTypes(c => ({ ...c, [k]: !c[k] }))}
                  className={clsx('inline-flex items-center gap-1.5 min-h-[34px] px-3 rounded-lg text-[13px] font-medium border transition-colors duration-150',
                    costTypes[k] ? 'border-primary-300 bg-primary-50 text-primary-700' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300')}>
                  {costTypes[k] && <Check size={12} />}{l}
                </button>
              ))}
            </div>
          </div>

          {/* Date range — the one range this tab uses */}
          <div className="flex flex-col gap-1">
            <span className={labelCls}>Shift date</span>
            <div className="flex items-center gap-2">
              <label htmlFor="gen-from" className="sr-only">From</label>
              <input id="gen-from" type="date" value={dateRange.start} onChange={e => setDateRange(r => ({ ...r, start: e.target.value }))} className="input-field !w-auto !py-2 text-sm" />
              <span className="text-slate-500" aria-hidden="true">–</span>
              <label htmlFor="gen-to" className="sr-only">To</label>
              <input id="gen-to" type="date" value={dateRange.end} onChange={e => setDateRange(r => ({ ...r, end: e.target.value }))} className="input-field !w-auto !py-2 text-sm" />
            </div>
          </div>

          <div className="flex items-end gap-2 ml-auto flex-wrap">
            <button type="button" onClick={loadPreview} className="btn-secondary">Refresh preview</button>
            <button type="button" onClick={handleGenerate} disabled={generating || targetRows.length === 0} className="btn-primary">
              {generating && <Loader2 size={14} className="animate-spin" />}
              {generateLabel}
            </button>
          </div>
        </div>
        <p className="text-xs text-slate-500 -mt-1">
          {selected.length > 0
            ? `${plural(selected.length, 'client')} ticked. Clear the ticks to invoice every client that is ready.`
            : 'With nothing ticked, Generate invoices every client with approved shifts. Tick clients to choose.'}
        </p>

        {/* Advanced */}
        <div>
          <button type="button" onClick={() => setShowAdvanced(a => !a)} aria-expanded={showAdvanced}
            className="flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800">
            {showAdvanced ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Advanced options
          </button>
          {showAdvanced && (
            <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
              <label className="flex items-center gap-2">
                <input type="checkbox" className="rounded" checked={advanced.includeZeroHour}
                  onChange={e => setAdvanced(a => ({ ...a, includeZeroHour: e.target.checked }))} />
                Include zero-hour shifts
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" className="rounded" checked={advanced.includeCancelled}
                  onChange={e => setAdvanced(a => ({ ...a, includeCancelled: e.target.checked }))} />
                Include cancelled shifts
              </label>
              <label className="flex items-center gap-2" title="NDIS lines are always GST-free per PAPL. This applies only to private lines.">
                <input type="checkbox" className="rounded" checked={advanced.autoGst}
                  onChange={e => setAdvanced(a => ({ ...a, autoGst: e.target.checked }))} />
                Auto-calculate GST (private lines only)
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" className="rounded" checked={advanced.groupBySupportItem}
                  onChange={e => setAdvanced(a => ({ ...a, groupBySupportItem: e.target.checked }))} />
                Group by support item number
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Preview table */}
      <div className="card overflow-hidden">
        {/* Search bar */}
        <div className="px-4 pt-3 pb-2.5 border-b border-slate-100">
          <SearchInput value={previewSearch} onChange={setPreviewSearch} placeholder="Search clients" className="max-w-xs" />
        </div>
        {previewError ? (
          <ErrorState message={previewError} onRetry={loadPreview} />
        ) : loadingPreview ? (
          <TableSkeleton rows={6} cols={6} />
        ) : preview.length === 0 ? (
          <EmptyState icon={Receipt} title="Nothing to invoice for these dates"
            hint="Shifts appear here once their timesheets are approved. Change the shift dates, or approve timesheets first."
            action={<Link to="/timesheets?status=pending" className="btn-secondary">Review timesheets</Link>} />
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white border-b border-slate-200">
                <th scope="col" className="w-10 pl-4 pr-1 py-2.5 text-left">
                  <input type="checkbox" className="rounded" aria-label="Select all clients"
                    checked={selected.length === preview.length && preview.length > 0}
                    onChange={selected.length === preview.length ? () => setSelected([]) : () => setSelected(preview.map(c => c.clientId))} />
                </th>
                <th scope="col" className={TH}>Client</th>
                <th scope="col" className={TH_NUM}>Shifts</th>
                <th scope="col" className={TH}>Status</th>
                <th scope="col" className={TH}>Invoice to</th>
                <th scope="col" className={TH}>Due</th>
                <th scope="col" className={clsx(TH_NUM, WIDE_ONLY)}>Mileage</th>
                <th scope="col" className={clsx(TH_NUM, WIDE_ONLY)}>Expenses</th>
                <th scope="col" className={clsx(TH, WIDE_ONLY)}>Purchase order</th>
                <th scope="col" className={clsx(TH, WIDE_ONLY)}>Non-shift PO</th>
                <th scope="col" className={clsx(TH, WIDE_ONLY, 'text-center')}>GST</th>
                <th scope="col" className={TH_NUM}>Total</th>
                <th scope="col" className="2xl:hidden w-12"><span className="sr-only">Invoice options</span></th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let lastStaff = null;
                return filteredPreview.map(client => {
                const rd = rowData[client.clientId] || {};
                const isSelected = selected.includes(client.clientId);
                const ready = isReady(client);
                const staffHeader = groupBy === 'staff' && client.primaryStaffName !== lastStaff
                  ? (lastStaff = client.primaryStaffName, client.primaryStaffName)
                  : (lastStaff = groupBy === 'staff' ? client.primaryStaffName : lastStaff, null);
                const extras = [
                  client.totalMileage > 0 ? `${num(client.totalMileage, 1)} km` : null,
                  client.totalExpense > 0 ? `${money(client.totalExpense)} expenses` : null,
                ].filter(Boolean).join(' · ');
                return (
                  <React.Fragment key={client.clientId}>
                  {staffHeader && (
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <td colSpan={13} className="px-4 py-2 text-xs font-semibold text-slate-700">
                        {staffHeader}
                      </td>
                    </tr>
                  )}
                  <tr className={clsx('border-b border-slate-100 transition-colors duration-150', isSelected ? 'bg-primary-50/50' : 'hover:bg-slate-50/60')}>
                    <td className="pl-4 pr-1 py-2.5">
                      <input type="checkbox" className="rounded" checked={isSelected} onChange={() => toggleSelect(client.clientId)}
                        aria-label={`Select ${client.clientName}`} />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <PersonAvatar id={client.clientId} name={client.clientName} size="sm" />
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 text-sm truncate">{client.clientName}</p>
                          {client.ndisNumber && <p className="text-xs text-slate-500">NDIS {client.ndisNumber}</p>}
                          {client.program === 'chsp' && <p className="text-xs text-teal-700">CHSP: participant contribution</p>}
                          {client.program === 'mixed' && <p className="text-xs text-teal-700">CHSP visits go on a separate invoice to the participant</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex flex-col items-end gap-0.5">
                        <div className="flex items-center gap-1.5">
                          {client.approvedShifts > 0 && (
                            <span className="badge-green !text-[10px] !px-1.5 !py-0.5 whitespace-nowrap">{client.approvedShifts} approved</span>
                          )}
                          <span className="text-sm font-semibold text-slate-800 tabular-nums whitespace-nowrap">{plural(client.totalShifts, 'shift')}</span>
                        </div>
                        <button type="button" onClick={() => setReportModal(client)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline">
                          <ExternalLink size={11} /> View report
                        </button>
                        {/* Aged care visits with no Support at Home price are left off the bill, never priced from the NDIS guide. */}
                        {client.notBilled?.length > 0 && (
                          <span className="text-[11px] text-amber-700 text-right max-w-[220px]" title={[...new Set(client.notBilled.map(n => n.reason))].join('\n')}>
                            {plural(client.notBilled.length, 'visit')} not billed. {client.notBilled[0].reason}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={clsx('whitespace-nowrap', ready ? 'badge-green' : client.totalShifts > 0 ? 'badge-yellow' : 'badge-gray')}>
                        {ready ? 'Ready' : client.totalShifts > 0 ? 'Not approved' : 'No shifts'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <select value={rd.payTo || 'client'} onChange={e => updateRow(client.clientId, 'payTo', e.target.value)}
                        aria-label={`Invoice to, ${client.clientName}`}
                        className="input-field !w-auto !py-1.5 !text-xs min-w-[120px]">
                        {PAY_TO_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2.5">
                      <input type="date" value={rd.dueDate || ''} onChange={e => updateRow(client.clientId, 'dueDate', e.target.value)}
                        aria-label={`Due date, ${client.clientName}`}
                        className="input-field !w-auto !py-1.5 !text-xs" />
                    </td>
                    <td className={clsx('px-3 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap', WIDE_ONLY)}>
                      {client.totalMileage > 0 ? `${num(client.totalMileage, 1)} km` : '—'}
                    </td>
                    <td className={clsx('px-3 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap', WIDE_ONLY)}>
                      {client.totalExpense > 0 ? money(client.totalExpense) : '—'}
                    </td>
                    <td className={clsx('px-3 py-2.5', WIDE_ONLY)}>
                      <input type="text" placeholder="PO number" value={rd.purchaseOrder || ''} onChange={e => updateRow(client.clientId, 'purchaseOrder', e.target.value)}
                        aria-label={`Purchase order, ${client.clientName}`}
                        className="input-field !py-1.5 !text-xs w-32" />
                    </td>
                    <td className={clsx('px-3 py-2.5', WIDE_ONLY)}>
                      <input type="text" placeholder="Non-shift PO" value={rd.nonShiftPO || ''} onChange={e => updateRow(client.clientId, 'nonShiftPO', e.target.value)}
                        aria-label={`Non-shift purchase order, ${client.clientName}`}
                        className="input-field !py-1.5 !text-xs w-32" />
                    </td>
                    <td className={clsx('px-3 py-2.5 text-center', WIDE_ONLY)}>
                      <input type="checkbox" checked={rd.includeTax || false} onChange={e => updateRow(client.clientId, 'includeTax', e.target.checked)}
                        aria-label={`Add GST for ${client.clientName}`} className="rounded" />
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <span className={clsx('text-sm tabular-nums', ready ? 'font-bold text-slate-900' : 'font-medium text-slate-500')}>{money(client.totalCost)}</span>
                      {!ready && client.totalShifts > 0 && <p className="text-[11px] text-slate-500">not yet approved</p>}
                      {extras && <p className="text-[11px] text-slate-500 2xl:hidden">incl. {extras}</p>}
                      {rd.includeTax && <p className="text-[11px] text-slate-500">plus GST</p>}
                    </td>
                    <td className="px-2 py-2.5 2xl:hidden">
                      <button type="button" onClick={() => setOptionsFor(client)} aria-label={`Invoice options for ${client.clientName}`}
                        title="Purchase order, non-shift PO and GST"
                        className={clsx('p-2 rounded-lg hover:bg-slate-100', (rd.purchaseOrder || rd.nonShiftPO || rd.includeTax) ? 'text-primary-700' : 'text-slate-500')}>
                        <SlidersHorizontal size={15} />
                      </button>
                    </td>
                  </tr>
                  </React.Fragment>
                );
                });
              })()}
            </tbody>
          </table>
        </div>
        )}
        {!loadingPreview && !previewError && preview.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap bg-slate-50/50">
            <p className="text-xs text-slate-600">
              Showing <strong>{filteredPreview.length}</strong> of <strong>{preview.length}</strong>
            </p>
            <p className="text-sm text-slate-700">
              Total in range: <strong className="text-slate-900 tabular-nums">{money(preview.reduce((s, c) => s + c.totalCost, 0))}</strong>
            </p>
          </div>
        )}
      </div>

      {/* Shift Report Modal */}
      {reportModal && (
        <ShiftReportModal client={reportModal} dateRange={dateRange} onClose={() => setReportModal(null)} />
      )}

      {optionsFor && (
        <InvoiceOptionsDialog client={optionsFor} rd={rowData[optionsFor.clientId] || {}}
          onChange={(field, val) => updateRow(optionsFor.clientId, field, val)}
          onClose={() => setOptionsFor(null)} />
      )}

      {/* Invoice View Modal (after generation) */}
      {viewInvoiceId && (
        <InvoiceDetailModal invoiceId={viewInvoiceId} onClose={() => setViewInvoice(null)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INVOICES TAB (the register) — one list with status and payer chips
// ─────────────────────────────────────────────────────────────────────────────
const LIST_PAGE_SIZE = 50;

const STATUS_CHIPS = ['draft', 'sent', 'overdue', 'paid', 'voided', 'invoiced', 'pending'];
const PAYER_CHIPS = ['ndia', 'plan_manager', 'self_managed', 'client', 'services_australia', 'aged_care_contribution'];

// Relative aging label for a due date, given the invoice status. Overdue reads red,
// so aged receivables are obvious at a glance without opening each invoice.
function dueMeta(dueDate, status) {
  if (!dueDate) return { label: '—', cls: 'text-slate-500' };
  const due = parseISO(String(dueDate).slice(0, 10));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((due - today) / 86400000);
  const dateStr = fmtDate(String(dueDate).slice(0, 10));
  if (status === 'paid' || status === 'voided') return { label: dateStr, cls: 'text-slate-500' };
  if (diff < 0)   return { label: dateStr, sub: `${Math.abs(diff)}d overdue`, cls: 'text-red-600', subCls: 'text-red-600' };
  if (diff === 0) return { label: dateStr, sub: 'due today', cls: 'text-amber-700', subCls: 'text-amber-700' };
  if (diff <= 7)  return { label: dateStr, sub: `in ${diff}d`, cls: 'text-slate-700', subCls: 'text-amber-700' };
  return { label: dateStr, sub: `in ${diff}d`, cls: 'text-slate-600', subCls: 'text-slate-500' };
}

const STATUS_WORD = { sent: 'sent', paid: 'paid', voided: 'void' };

function ListTab({ onGoGenerate, onExportNdis }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const showClaimErrors = useClaimErrors();
  const [invoices, setInvoices]     = useState([]);
  const [stats, setStats]           = useState({});
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState(null);
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatus]   = useUrlTab('status', '', STATUS_CHIPS);
  const [paymentType, setPayer]     = useUrlTab('payer', '', PAYER_CHIPS);
  const [clientFilter, setClientFilter] = useState('');
  const [clients, setClients]       = useState([]);
  const [viewId, setViewId]         = useState(null);
  const [anomalies, setAnomalies]   = useState({});
  const [ndiaSubmittingId, setNdiaSubmittingId] = useState(null);
  const [page, setPage]             = useState(1);
  const [total, setTotal]           = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy]     = useState(false);
  const [aiCheckResult, setAiCheckResult]     = useState(null);
  const [aiCheckLoading, setAiCheckLoading]   = useState(false);
  const [aiCheckInvoiceId, setAiCheckInvoiceId] = useState(null);

  const checkInvoice = async (invoiceId) => {
    setAiCheckInvoiceId(invoiceId);
    setAiCheckLoading(true);
    setAiCheckResult(null);
    try {
      const { data } = await api.post('/ai/check-invoice', { invoiceId });
      setAiCheckResult(data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'AI check failed');
      setAiCheckInvoiceId(null);
    } finally {
      setAiCheckLoading(false);
    }
  };

  useEffect(() => {
    api.get('/clients').then(r => setClients(r.data?.clients || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (statusFilter) p.set('status', statusFilter);
    if (paymentType) p.set('payment_type', paymentType);
    if (clientFilter) p.set('client_id', clientFilter);
    p.set('page', page);
    p.set('limit', LIST_PAGE_SIZE);
    Promise.all([
      api.get(`/invoices?${p}`),
      api.get('/invoices/stats/summary'),
      api.get('/invoices/anomalies'),
    ]).then(([r1, r2, r3]) => {
      const data = r1.data;
      if (Array.isArray(data)) {
        setInvoices(data); setTotal(data.length);
      } else {
        setInvoices(data.invoices || []); setTotal(data.total || 0);
      }
      setStats(r2.data);
      setAnomalies(r3.data || {});
    })
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load invoices. Check your connection, then try again."))
      .finally(() => setLoading(false));
  }, [search, statusFilter, paymentType, clientFilter, page]);

  useEffect(() => { setPage(1); }, [search, statusFilter, paymentType, clientFilter]);
  // A selection belongs to the list on screen; a new filter or page starts a new one, so a bulk
  // action's count and total are always the rows you can see.
  useEffect(() => { setSelectedIds([]); }, [search, statusFilter, paymentType, clientFilter, page]);
  useEffect(() => { load(); }, [load]);

  // Void and Paid change receivables, so they ask first and say what they change.
  const updateStatus = async (inv, status) => {
    const amount = money(inv.total_amount);
    if (status === 'voided') {
      const paid = ['paid', 'partially_paid'].includes(inv.status);
      const ok = await confirm(paid
        ? `${inv.client_name} · ${amount}. This invoice is PAID. Voiding it gives its funding back to the budget and its shifts become billable again, as if the money never came in. Only void a paid invoice that was raised by mistake; for money paid back, raise a credit note instead.`
        : `${inv.client_name} · ${amount}. A voided invoice cannot be claimed, and its shifts become billable again. An admin can undo it if it was a mistake.`,
        { title: paid ? `Void PAID invoice ${inv.invoice_number}?` : `Void invoice ${inv.invoice_number}?`, confirmLabel: paid ? 'Void the paid invoice' : 'Void invoice', danger: true });
      if (!ok) return;
    } else if (status === 'paid') {
      const ok = await confirm(`${inv.client_name} · ${amount}. It moves to Paid and counts as collected. To record the amount received and a reference, use Record payment on the invoice instead.`,
        { title: `Mark invoice ${inv.invoice_number} as paid?`, confirmLabel: 'Mark paid' });
      if (!ok) return;
    }
    try {
      await api.patch(`/invoices/${inv.id}/status`, { status });
      toast.success(`Invoice ${inv.invoice_number} marked as ${STATUS_WORD[status] || status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not change the status');
    }
  };

  // Voided by mistake: restore it (as paid, or as sent), taking its funding again.
  const undoVoid = async (inv) => {
    const asPaid = await confirm(`${inv.client_name} · ${money(inv.total_amount)}. Its funding is taken from the budget again, and its weekly claims come back with it. Choose Restore as paid if the money has come in; Cancel to restore it as sent instead.`,
      { title: `Undo void of ${inv.invoice_number}?`, confirmLabel: 'Restore as paid' });
    let status = 'paid';
    if (!asPaid) {
      const asSent = await confirm('Restore it as sent (waiting for payment)?', { title: `Restore ${inv.invoice_number} as sent?`, confirmLabel: 'Restore as sent' });
      if (!asSent) return;
      status = 'sent';
    }
    try {
      const { data } = await api.post(`/invoices/${inv.id}/undo-void`, { status });
      toast.success(`Invoice ${inv.invoice_number} restored as ${STATUS_WORD[status] || status}${data?.funding_taken_again ? ` · ${money(data.funding_taken_again)} taken from the budget again` : ''}`);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not restore the invoice'); }
  };

  const deleteInvoice = async (inv) => {
    const ok = await confirm(`${inv.client_name} · ${money(inv.total_amount)}. This cannot be undone.`,
      { title: `Delete invoice ${inv.invoice_number}?`, confirmLabel: 'Delete invoice', danger: true });
    if (!ok) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success(`Invoice ${inv.invoice_number} deleted`);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to delete invoice'); }
  };

  const bulkUpdateStatus = async (status) => {
    if (!selectedIds.length) return;
    const ids = [...selectedIds];
    const n = ids.length;
    const rows = invoices.filter(i => ids.includes(i.id));
    const sum = money(rows.reduce((s, i) => s + (Number(i.total_amount) || 0), 0));
    const copy = {
      sent:   { title: `Mark ${plural(n, 'invoice')} totalling ${sum} as sent?`, body: 'They move to Sent and start counting as outstanding. Nothing is emailed.', label: `Mark ${n} sent` },
      paid:   { title: `Mark ${plural(n, 'invoice')} totalling ${sum} as paid?`, body: 'They move to Paid and count as collected. No amount or reference is recorded; use Record payment on an invoice for that.', label: `Mark ${n} paid` },
      voided: { title: `Void ${plural(n, 'invoice')} totalling ${sum}?`, body: 'Voided invoices cannot be claimed, and their shifts become billable again. This cannot be undone.', label: `Void ${n}`, danger: true },
    }[status];
    const ok = await confirm(copy.body, { title: copy.title, confirmLabel: copy.label, danger: !!copy.danger });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(ids.map(id => api.patch(`/invoices/${id}/status`, { status })));
      const failed = [];
      let reason = null;
      results.forEach((r, i) => {
        if (r.status === 'rejected') { failed.push(ids[i]); reason = reason || r.reason?.response?.data?.message || null; }
      });
      const done = n - failed.length;
      if (!failed.length) toast.success(`${plural(done, 'invoice')} marked as ${STATUS_WORD[status] || status}`);
      else if (!done) toast.error(`None of the ${plural(n, 'invoice')} changed${reason ? `: ${reason}` : ''}`);
      else toast(`${done} updated, ${failed.length} failed${reason ? `: ${reason}` : ''}. The failed ones are still selected.`, { icon: warnIcon, duration: 8000 });
      // The ones that failed stay ticked, so they can be looked at or tried again.
      setSelectedIds(failed);
      load();
    } finally { setBulkBusy(false); }
  };

  const bulkDownloadProda = async () => {
    if (!selectedIds.length) return;
    try {
      const params = new URLSearchParams({ invoice_ids: selectedIds.join(',') });
      const resp = await fetch(`/api/invoices/proda-export?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      // A claim-validation refusal carries per-line detail; show that instead of a bare summary.
      const err = await readClaimRefusal(resp, showClaimErrors);
      if (err === true) return;
      const blob = await resp.blob();
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = `PRODA_bulk_${new Date().toISOString().slice(0,10)}.csv`; a.click();
      URL.revokeObjectURL(a.href);
      toast.success('PRODA CSV downloaded');
    } catch { toast.error('Export failed'); }
  };

  const downloadProdaSingle = async (invoiceId) => {
    try {
      const params = new URLSearchParams({ invoice_ids: invoiceId });
      const resp = await fetch(`/api/invoices/proda-export?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!resp.ok) {
        // A claim-validation refusal carries per-line detail; show that instead of a bare summary.
        const err = await readClaimRefusal(resp, showClaimErrors);
        if (err === true) return;
        toast.error(err.message || 'No NDIS line items on this invoice');
        return;
      }
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `PRODA_${invoiceId.slice(0,8)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('PRODA CSV downloaded');
    } catch { toast.error('Export failed'); }
  };

  const submitNdiaSingle = async (inv) => {
    const ok = await confirm(`${inv.client_name} · ${money(inv.total_amount)}. Its NDIA lines are sent to the NDIA as a payment request.`,
      { title: `Submit invoice ${inv.invoice_number} to the NDIA?`, confirmLabel: 'Submit to NDIA' });
    if (!ok) return;
    setNdiaSubmittingId(inv.id);
    try {
      const r = await api.post('/integrations/ndia/submit-claims', { invoiceIds: [inv.id] });
      toast.success(`${r.data.submitted} claim${r.data.submitted !== 1 ? 's' : ''} submitted to NDIA`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'NDIA submission failed — check credentials in Settings');
    } finally { setNdiaSubmittingId(null); }
  };

  const cnt = (k) => Number(stats[k] || 0);
  const outstandingCount = cnt('sent') + cnt('overdue');

  const statusOptions = [
    { value: '', label: 'All' },
    { value: 'draft', label: 'Draft', count: stats.draft != null ? cnt('draft') : undefined },
    { value: 'sent', label: 'Sent', count: stats.sent != null ? cnt('sent') : undefined },
    { value: 'overdue', label: 'Overdue', count: stats.overdue != null ? cnt('overdue') : undefined, tone: cnt('overdue') > 0 ? 'rose' : undefined },
    { value: 'paid', label: 'Paid', count: stats.paid != null ? cnt('paid') : undefined },
    { value: 'voided', label: 'Void', count: stats.voided != null ? cnt('voided') : undefined },
    { value: 'invoiced', label: 'Invoiced' },
    { value: 'pending', label: 'Pending' },
  ];
  const payerOptions = [
    { value: '', label: 'All payers' },
    { value: 'ndia', label: 'NDIA', count: stats.ndia_count != null ? cnt('ndia_count') : undefined },
    { value: 'plan_manager', label: 'Plan manager', count: stats.plan_manager_count != null ? cnt('plan_manager_count') : undefined },
    { value: 'self_managed', label: 'Self-managed' },
    { value: 'client', label: 'Client', count: stats.client_count != null ? cnt('client_count') : undefined },
    { value: 'services_australia', label: 'Services Australia' },
    { value: 'aged_care_contribution', label: 'Aged care contribution' },
  ];

  const allOnPage = invoices.length > 0 && invoices.every(i => selectedIds.includes(i.id));
  const pages = Math.max(1, Math.ceil(total / LIST_PAGE_SIZE));

  return (
    <div className="space-y-4">
      {/* Receivables summary — money first */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Outstanding" accent="primary" value={money(stats.total_outstanding)}
          sub={`${plural(outstandingCount, 'invoice')} awaiting payment`} />
        <KpiTile label="Overdue" accent="slate" alert={cnt('overdue') > 0} value={money(stats.total_overdue)}
          sub={cnt('overdue') ? `${plural(cnt('overdue'), 'invoice')} past the due date` : 'Nothing past due'} />
        <KpiTile label="Collected" accent="emerald" value={money(stats.total_paid)}
          sub={`${plural(cnt('paid'), 'invoice')} paid to date`} />
        <KpiTile label="Draft" accent="slate" value={money(stats.total_draft)}
          sub={`${plural(cnt('draft'), 'invoice')} not yet sent`} />
      </div>

      {/* Filters */}
      <div className="card p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search by invoice number or client" className="flex-1 min-w-[220px] max-w-md" />
          <label htmlFor="inv-client-filter" className="sr-only">Client</label>
          <select id="inv-client-filter" value={clientFilter} onChange={e => setClientFilter(e.target.value)}
            className="input-field !w-auto !py-1.5 text-[13px] min-w-[170px]">
            <option value="">All clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
          </select>
          <button type="button" onClick={onExportNdis} className="btn-secondary ml-auto" title="Every invoice line with the shift and timesheet it came from">
            <FileDown size={14} /> Export NDIS lines
          </button>
        </div>
        <FilterChips ariaLabel="Invoice status" multi={false} options={statusOptions} value={statusFilter || null} onChange={(v) => setStatus(v || '')} />
        <FilterChips ariaLabel="Payer" multi={false} options={payerOptions} value={paymentType || null} onChange={(v) => setPayer(v || '')} />
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loadError ? (
          <ErrorState message={loadError} onRetry={load} />
        ) : loading ? (
          <TableSkeleton rows={8} cols={7} />
        ) : invoices.length === 0 ? (
          <EmptyState icon={Receipt} title="No invoices match these filters"
            hint="Clear a filter, or create invoices from approved shifts on the Generate tab."
            action={<button type="button" onClick={onGoGenerate} className="btn-secondary">Generate invoices</button>} />
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-white border-b border-slate-200">
                <th scope="col" className="w-10 pl-4 pr-1 py-2.5 text-left">
                  <input type="checkbox" className="rounded" aria-label="Select every invoice on this page"
                    checked={allOnPage}
                    onChange={() => setSelectedIds(allOnPage ? [] : invoices.map(i => i.id))} />
                </th>
                <th scope="col" className={TH}>Invoice</th>
                <th scope="col" className={TH}>Client</th>
                <th scope="col" className={clsx(TH, 'hidden lg:table-cell')}>Issued</th>
                <th scope="col" className={TH}>Due</th>
                <th scope="col" className={clsx(TH, 'hidden xl:table-cell')}>Payer</th>
                <th scope="col" className={TH_NUM}>Amount</th>
                <th scope="col" className={TH}>Status</th>
                <th scope="col" className={TH_NUM}><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => {
                const anomaly = anomalies[inv.id];
                const isSelected = selectedIds.includes(inv.id);
                const due = dueMeta(inv.due_date, inv.status);
                return (
                  <tr key={inv.id} className={clsx('group border-b border-slate-100 last:border-0 cursor-pointer',
                    isSelected ? 'bg-primary-50/40' : anomaly ? 'bg-amber-50/40' : 'hover:bg-slate-50/70')}
                    onClick={() => setViewId(inv.id)}>
                    <td className="pl-4 pr-1 py-2.5" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" className="rounded" checked={isSelected}
                        aria-label={`Select invoice ${inv.invoice_number}`}
                        onChange={() => setSelectedIds(s => s.includes(inv.id) ? s.filter(x => x !== inv.id) : [...s, inv.id])} />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[13px] font-semibold text-slate-900 group-hover:text-primary-700 group-hover:underline whitespace-nowrap">
                          {inv.invoice_number}
                        </span>
                        {anomaly && (
                          <span title={anomaly.flags.join(' · ')} className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-full cursor-help shrink-0">
                            <AlertCircle size={9}/> Flag
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <PersonAvatar id={inv.client_id} name={inv.client_name} size="xs" />
                        <span className="font-medium text-slate-800 truncate">{inv.client_name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600 text-xs whitespace-nowrap hidden lg:table-cell">{inv.invoice_date ? fmtDate(String(inv.invoice_date).slice(0, 10)) : '—'}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div className={clsx('text-xs font-medium', due.cls)}>{due.label}</div>
                      {due.sub && <div className={clsx('text-[11px] font-semibold', due.subCls)}>{due.sub}</div>}
                    </td>
                    <td className="px-3 py-2.5 hidden xl:table-cell">
                      <span className="badge-gray whitespace-nowrap">{payTypeLabel(inv.payment_type)}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <span className="font-semibold text-slate-900 tabular-nums whitespace-nowrap">{money(inv.total_amount)}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <StatusPill status={inv.status} />
                    </td>
                    <td className="px-3 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => setViewId(inv.id)} aria-label={`View invoice ${inv.invoice_number}`}
                          className="btn-secondary btn-sm">
                          <Eye size={12} /> View
                        </button>
                        <OverflowMenu label={`Actions for invoice ${inv.invoice_number}`} items={[
                          isNdiaPayer(inv.payment_type) && { label: 'Download PRODA CSV', icon: Download, onClick: () => downloadProdaSingle(inv.id) },
                          isNdiaPayer(inv.payment_type) && { label: ndiaSubmittingId === inv.id ? 'Submitting…' : 'Submit to NDIA', icon: Zap, disabled: ndiaSubmittingId === inv.id, onClick: () => submitNdiaSingle(inv) },
                          isSaPayer(inv.payment_type) && { label: 'Claim from Services Australia', icon: ExternalLink, onClick: () => navigate(AGED_CARE_CLAIMS) },
                          { label: 'AI invoice check', icon: Sparkles, disabled: aiCheckLoading && aiCheckInvoiceId === inv.id, onClick: () => checkInvoice(inv.id) },
                          { divider: true },
                          { label: 'Mark as sent', icon: Send, onClick: () => updateStatus(inv, 'sent') },
                          { label: 'Mark as paid', icon: CheckCircle, onClick: () => updateStatus(inv, 'paid') },
                          inv.status === 'voided'
                            ? { label: 'Undo void', icon: Undo2, onClick: () => undoVoid(inv) }
                            : { label: 'Void invoice', icon: Ban, danger: true, onClick: () => updateStatus(inv, 'voided') },
                          { divider: true },
                          { label: 'Delete invoice', icon: Trash2, danger: true, onClick: () => deleteInvoice(inv) },
                        ]} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
        {!loading && !loadError && invoices.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-xs text-slate-600">Showing {invoices.length} of {plural(total, 'invoice')}</p>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="btn-secondary btn-sm">Previous</button>
              <span className="text-xs text-slate-600 tabular-nums">Page {page} of {pages}</span>
              <button type="button" onClick={() => setPage(p => p + 1)} disabled={page >= pages} className="btn-secondary btn-sm">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk actions for the ticked invoices */}
      <BulkBar count={selectedIds.length} onClear={() => setSelectedIds([])} noun={`invoice${selectedIds.length === 1 ? '' : 's'} selected`}>
        <button type="button" onClick={() => bulkUpdateStatus('sent')} disabled={bulkBusy} className="btn-secondary btn-sm"><Send size={12} /> Mark sent</button>
        <button type="button" onClick={() => bulkUpdateStatus('paid')} disabled={bulkBusy} className="btn-secondary btn-sm"><CheckCircle size={12} /> Mark paid</button>
        <button type="button" onClick={() => bulkUpdateStatus('voided')} disabled={bulkBusy}
          className="btn-secondary btn-sm !text-rose-700 !border-rose-300 hover:!bg-rose-50"><Ban size={12} /> Void</button>
        <button type="button" onClick={bulkDownloadProda} disabled={bulkBusy} className="btn-secondary btn-sm"><Download size={12} /> PRODA CSV</button>
      </BulkBar>

      {viewId && <InvoiceDetailModal invoiceId={viewId} onClose={() => setViewId(null)} onSaved={load} />}

      {/* AI check results */}
      {aiCheckResult && (
        <Dialog open size="sm" title="AI invoice check" onClose={() => { setAiCheckResult(null); setAiCheckInvoiceId(null); }}
          footer={<button type="button" onClick={() => { setAiCheckResult(null); setAiCheckInvoiceId(null); }} className="btn-secondary">Close</button>}>
          <div className="space-y-4">
            {/* Overall risk badge */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-600">Overall risk</span>
              <span className={clsx(
                aiCheckResult.overallRisk === 'high' ? 'badge-red' :
                aiCheckResult.overallRisk === 'medium' || aiCheckResult.overallRisk === 'low' ? 'badge-yellow' :
                'badge-green')}>
                {aiCheckResult.overallRisk === 'none' || !aiCheckResult.overallRisk ? 'None' : codeWord(aiCheckResult.overallRisk)}
              </span>
            </div>
            {/* Summary */}
            <p className="text-sm text-slate-700 bg-slate-50 rounded-xl px-4 py-3">{aiCheckResult.summary}</p>
            {/* Anomalies list */}
            {aiCheckResult.anomalies?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-600">Issues found</p>
                {aiCheckResult.anomalies.map((a, i) => (
                  <div key={i} className="flex items-start gap-2 bg-slate-50 rounded-xl px-3 py-2.5">
                    <span className={clsx('shrink-0 mt-0.5', a.severity === 'high' ? 'badge-red' : 'badge-yellow', '!text-[10px]')}>
                      {codeWord(a.severity)}
                    </span>
                    <div>
                      {a.field && <p className="text-xs font-semibold text-slate-800">{a.field}</p>}
                      <p className="text-xs text-slate-700">{a.issue}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
}

const codeWord = (s) => { const v = String(s || ''); return v ? v.charAt(0).toUpperCase() + v.slice(1) : ''; };

// ─────────────────────────────────────────────────────────────────────────────
// ACCOUNTING TAB — Export invoices to Xero / MYOB
// ─────────────────────────────────────────────────────────────────────────────
function ExportPreviewTable({ rows, withPayer }) {
  return (
    <div className="border border-slate-200 rounded-xl overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-white border-b border-slate-200">
            <th scope="col" className={TH}>Invoice</th>
            <th scope="col" className={TH}>Client</th>
            <th scope="col" className={TH}>Date</th>
            {withPayer && <th scope="col" className={TH}>Payer</th>}
            <th scope="col" className={TH}>Status</th>
            <th scope="col" className={TH_NUM}>Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.slice(0, 20).map(inv => (
            <tr key={inv.id}>
              <td className="px-3 py-1.5 font-mono text-slate-700 whitespace-nowrap">{inv.invoice_number}</td>
              <td className="px-3 py-1.5 text-slate-800">{inv.client_name}</td>
              <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">{inv.invoice_date ? fmtDate(String(inv.invoice_date).slice(0, 10)) : '—'}</td>
              {withPayer && <td className="px-3 py-1.5 text-slate-600">{payTypeLabel(inv.payment_type)}</td>}
              <td className="px-3 py-1.5"><StatusPill status={inv.status} className="!text-[10px]" /></td>
              <td className="px-3 py-1.5 text-right tabular-nums font-medium text-slate-900 whitespace-nowrap">{money(inv.total_amount)}</td>
            </tr>
          ))}
          {rows.length > 20 && (
            <tr>
              <td colSpan={withPayer ? 6 : 5} className="px-3 py-2 text-center text-slate-600 italic">
                + {rows.length - 20} more invoices included in the export
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function XeroTab() {
  const today = new Date();
  const monthAgo = new Date(today); monthAgo.setMonth(monthAgo.getMonth() - 1);
  const fmt = isoDate;

  const [clients, setClients] = useState([]);

  // Xero state
  const [xeroRange, setXeroRange] = useState({ start: fmt(monthAgo), end: fmt(today) });
  const [xeroStatus, setXeroStatus] = useState('');
  const [xeroClientId, setXeroClientId] = useState('');
  const [xeroInvoices, setXeroInvoices] = useState([]);
  const [xeroLoading, setXeroLoading] = useState(false);
  const [xeroDownloading, setXeroDownloading] = useState(false);

  // MYOB state
  const [myobRange, setMyobRange] = useState({ start: fmt(monthAgo), end: fmt(today) });
  const [myobStatus, setMyobStatus] = useState('');
  const [myobClientId, setMyobClientId] = useState('');
  const [myobPaymentType, setMyobPaymentType] = useState('');
  const [myobInvoices, setMyobInvoices] = useState([]);
  const [myobLoading, setMyobLoading] = useState(false);
  const [myobDownloading, setMyobDownloading] = useState(false);

  useEffect(() => {
    api.get('/clients').then(r => setClients(r.data?.clients || [])).catch(() => {});
  }, []);

  const loadXero = async () => {
    setXeroLoading(true);
    try {
      const params = new URLSearchParams({ start_date: xeroRange.start, end_date: xeroRange.end });
      if (xeroStatus) params.set('status', xeroStatus);
      if (xeroClientId) params.set('client_id', xeroClientId);
      const { data } = await api.get(`/invoices?${params}`);
      setXeroInvoices(Array.isArray(data) ? data : (data.invoices || []));
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to load invoices'); }
    finally { setXeroLoading(false); }
  };

  const loadMyob = async () => {
    setMyobLoading(true);
    try {
      const params = new URLSearchParams({ start_date: myobRange.start, end_date: myobRange.end });
      if (myobStatus) params.set('status', myobStatus);
      if (myobClientId) params.set('client_id', myobClientId);
      if (myobPaymentType) params.set('payment_type', myobPaymentType);
      const { data } = await api.get(`/invoices?${params}`);
      setMyobInvoices(Array.isArray(data) ? data : (data.invoices || []));
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to load invoices'); }
    finally { setMyobLoading(false); }
  };

  useEffect(() => { loadXero(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadMyob(); /* eslint-disable-next-line */ }, []);

  const downloadXero = async () => {
    setXeroDownloading(true);
    try {
      const params = new URLSearchParams({ start_date: xeroRange.start, end_date: xeroRange.end });
      if (xeroStatus) params.set('status', xeroStatus);
      if (xeroClientId) params.set('client_id', xeroClientId);
      const res = await api.get(`/invoices/xero-export?${params}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `xero-invoices-${xeroRange.start}-to-${xeroRange.end}.csv`;
      a.click(); URL.revokeObjectURL(a.href);
      toast.success('Xero CSV downloaded. In Xero: Accounts › Sales › Import.');
    } catch (e) { toast.error(e.response?.data?.message || 'Export failed'); }
    finally { setXeroDownloading(false); }
  };

  const downloadMyob = async () => {
    setMyobDownloading(true);
    try {
      const params = new URLSearchParams({ start_date: myobRange.start, end_date: myobRange.end });
      if (myobStatus) params.set('status', myobStatus);
      if (myobClientId) params.set('client_id', myobClientId);
      if (myobPaymentType) params.set('payment_type', myobPaymentType);
      const res = await api.get(`/invoices/myob-export?${params}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `myob-invoices-${myobRange.start}-to-${myobRange.end}.csv`;
      a.click(); URL.revokeObjectURL(a.href);
      toast.success('MYOB CSV downloaded. In MYOB: File › Import Data › Sales › Invoices.');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Export failed — no invoices match the selected filters');
    }
    finally { setMyobDownloading(false); }
  };

  const xeroTotal = xeroInvoices.reduce((s, i) => s + Number(i.total_amount || 0), 0);
  const myobTotal = myobInvoices.reduce((s, i) => s + Number(i.total_amount || 0), 0);
  const lbl = 'text-[13px] text-slate-700 font-medium block mb-1';

  return (
    <div className="space-y-4">
      {/* Xero Export */}
      <div className="card p-5 sm:p-6 space-y-4">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
            <Download size={18} className="text-slate-600" />
          </span>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-slate-900">Xero: accounts receivable import</h3>
            <p className="text-sm text-slate-600 mt-1">
              A Xero-compatible CSV with full line-item detail. NDIS lines are marked GST-free automatically.
            </p>
          </div>
        </div>

        <Notice tone="slate" title="Xero API sync is available"
          actions={<Link to="/settings?tab=integrations" className="btn-secondary btn-sm">Go to integrations</Link>}>
          Connect your Xero account in Settings › Integrations to send invoices straight to Xero, with no CSV upload.
        </Notice>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 pt-3 border-t border-slate-100">
          <div>
            <label htmlFor="xero-from" className={lbl}>From</label>
            <input id="xero-from" type="date" value={xeroRange.start} onChange={e => setXeroRange(r => ({ ...r, start: e.target.value }))} className="input-field" />
          </div>
          <div>
            <label htmlFor="xero-to" className={lbl}>To</label>
            <input id="xero-to" type="date" value={xeroRange.end} onChange={e => setXeroRange(r => ({ ...r, end: e.target.value }))} className="input-field" />
          </div>
          <div>
            <label htmlFor="xero-status" className={lbl}>Status</label>
            <select id="xero-status" className="input-field" value={xeroStatus} onChange={e => setXeroStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="draft">Draft</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          <div>
            <label htmlFor="xero-client" className={lbl}>Client</label>
            <select id="xero-client" className="input-field" value={xeroClientId} onChange={e => setXeroClientId(e.target.value)}>
              <option value="">All clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button type="button" onClick={loadXero} disabled={xeroLoading} className="btn-secondary">
              {xeroLoading ? <Loader2 size={13} className="animate-spin" /> : 'Refresh'}
            </button>
            <button type="button" onClick={downloadXero} disabled={!xeroInvoices.length || xeroDownloading} className="btn-primary flex-1">
              {xeroDownloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
              Xero CSV
            </button>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700">
          <strong className="text-slate-900">{xeroInvoices.length}</strong> invoice{xeroInvoices.length !== 1 ? 's' : ''} in range ·
          Total: <strong className="text-slate-900 tabular-nums">{money(xeroTotal)}</strong>
        </div>

        {xeroInvoices.length > 0 && <ExportPreviewTable rows={xeroInvoices} />}
      </div>

      {/* MYOB Export */}
      <div className="card p-5 sm:p-6 space-y-4">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
            <Download size={18} className="text-slate-600" />
          </span>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-slate-900">MYOB: accounts receivable import</h3>
            <p className="text-sm text-slate-600 mt-1">
              A MYOB-compatible CSV with split name columns and the right tax codes (GST / FRE). In MYOB: <em>File › Import Data › Sales › Invoices</em>.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 pt-3 border-t border-slate-100">
          <div>
            <label htmlFor="myob-from" className={lbl}>From</label>
            <input id="myob-from" type="date" value={myobRange.start} onChange={e => setMyobRange(r => ({ ...r, start: e.target.value }))} className="input-field" />
          </div>
          <div>
            <label htmlFor="myob-to" className={lbl}>To</label>
            <input id="myob-to" type="date" value={myobRange.end} onChange={e => setMyobRange(r => ({ ...r, end: e.target.value }))} className="input-field" />
          </div>
          <div>
            <label htmlFor="myob-status" className={lbl}>Status</label>
            <select id="myob-status" className="input-field" value={myobStatus} onChange={e => setMyobStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="draft">Draft</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          <div>
            <label htmlFor="myob-client" className={lbl}>Client</label>
            <select id="myob-client" className="input-field" value={myobClientId} onChange={e => setMyobClientId(e.target.value)}>
              <option value="">All clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="myob-payer" className={lbl}>Payer</label>
            <select id="myob-payer" className="input-field" value={myobPaymentType} onChange={e => setMyobPaymentType(e.target.value)}>
              <option value="">All payers</option>
              <option value="ndia">NDIA</option>
              <option value="plan_manager">Plan manager</option>
              <option value="client">Client</option>
              <option value="self_managed">Self-managed</option>
              <option value="services_australia">Services Australia</option>
              <option value="aged_care_contribution">Aged care contribution</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-slate-700">
            <strong className="text-slate-900">{myobInvoices.length}</strong> invoice{myobInvoices.length !== 1 ? 's' : ''} ·
            Total: <strong className="text-slate-900 tabular-nums">{money(myobTotal)}</strong>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={loadMyob} disabled={myobLoading} className="btn-secondary">
              {myobLoading ? <Loader2 size={13} className="animate-spin" /> : 'Refresh'}
            </button>
            <button type="button" onClick={downloadMyob} disabled={!myobInvoices.length || myobDownloading} className="btn-secondary">
              {myobDownloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
              MYOB CSV
            </button>
          </div>
        </div>

        {myobInvoices.length > 0 && <ExportPreviewTable rows={myobInvoices} withPayer />}
      </div>

      {/* QuickBooks note */}
      <div className="card p-5 border-dashed">
        <p className="text-sm text-slate-600">
          <strong className="text-slate-800">QuickBooks and other software</strong>: the Xero CSV format imports into most accounting platforms through their import wizard. Use the Xero CSV above and map the columns during import.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CLAIMS TAB — NDIA direct submit + PRODA export
// ─────────────────────────────────────────────────────────────────────────────
function NdiaSubmitTab() {
  const confirm = useConfirm();
  const today = new Date();
  const monthAgo = new Date(today); monthAgo.setMonth(monthAgo.getMonth() - 1);
  const fmt = isoDate;
  const [range, setRange] = useState({ start: fmt(monthAgo), end: fmt(today) });
  const [ndiaStatus, setNdiaStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    api.get('/integrations/ndia/status').then(r => setNdiaStatus(r.data)).catch(() => {});
  }, []);

  const submitClaims = async () => {
    const ok = await confirm(`Every NDIA invoice marked Approved or Sent and dated ${fmtDate(range.start)} – ${fmtDate(range.end)} is sent to the NDIA as payment requests.`, {
      title: 'Submit claims to the NDIA?', confirmLabel: 'Submit claims',
    });
    if (!ok) return;
    setSubmitting(true);
    setLastResult(null);
    try {
      const { data } = await api.post('/integrations/ndia/submit-claims', {
        dateFrom: range.start, dateTo: range.end,
      });
      setLastResult(data);
      toast.success(data.message);
    } catch (e) {
      const msg = e.response?.data?.message || 'Submission failed';
      toast.error(msg);
      setLastResult({ error: msg });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="card p-5 sm:p-6 space-y-5">
      <div>
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <Zap size={16} className="text-slate-500" /> Direct NDIA claim submission
        </h3>
        <p className="text-sm text-slate-600 mt-1">
          Submit approved NDIA invoices straight to the NDIA through the B2B provider API, with no manual PRODA CSV upload.
        </p>
      </div>

      {!ndiaStatus?.connected ? (
        <Notice tone="amber" title="The NDIA API isn't set up">
          Go to Settings › Integrations › NDIA direct API to add your B2B connector credentials.
        </Notice>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap bg-slate-50 border border-slate-200 rounded-xl p-3">
            <span className="badge-green">Connected</span>
            <span className={ndiaStatus.environment === 'production' ? 'badge-green' : 'badge-yellow'}>
              {ndiaStatus.environment === 'production' ? 'Live' : 'Sandbox'}
            </span>
            {ndiaStatus.providerNumber && <span className="text-xs text-slate-600">Provider {ndiaStatus.providerNumber}</span>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="ndia-from" className="input-label">From date</label>
              <input id="ndia-from" type="date" className="input-field" value={range.start} onChange={e => setRange(r => ({ ...r, start: e.target.value }))} />
            </div>
            <div>
              <label htmlFor="ndia-to" className="input-label">To date</label>
              <input id="ndia-to" type="date" className="input-field" value={range.end} onChange={e => setRange(r => ({ ...r, end: e.target.value }))} />
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-700 space-y-1">
            <p className="font-semibold text-slate-800">What gets submitted</p>
            <p>• Invoices with status <strong>Approved</strong> or <strong>Sent</strong></p>
            <p>• Payer <strong>NDIA</strong></p>
            <p>• Line items with a <strong>support item number</strong> (e.g. 01_011_0107_1_1)</p>
            <p>• Clients with an <strong>NDIS number</strong> on their profile</p>
          </div>

          <button type="button" onClick={submitClaims} disabled={submitting} className="btn-primary w-full py-3">
            {submitting ? <><Loader2 size={15} className="animate-spin" /> Submitting to the NDIA…</> : <><Zap size={15} /> Submit claims to the NDIA</>}
          </button>

          {lastResult && !lastResult.error && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm">
              <p className="font-semibold text-emerald-900 mb-1 flex items-center gap-1.5"><CheckCircle size={15} /> Submitted</p>
              <p className="text-emerald-800">{lastResult.message}</p>
              {lastResult.response && <p className="text-xs text-emerald-800 mt-1">NDIA response status: {lastResult.status}</p>}
            </div>
          )}
          {lastResult?.error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800" role="alert">{lastResult.error}</div>
          )}
        </>
      )}

      <div className="border-t border-slate-100 pt-4">
        <p className="text-xs font-semibold text-slate-600 mb-3">How it compares to a PRODA CSV</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          {[
            ['NDIA direct API', ['Instant submission', 'Real-time status', 'No portal login needed', 'Requires B2B registration']],
            ['PRODA CSV upload', ['Always available', 'No set-up required', 'Manual upload to the portal', 'Status visible in the PRODA portal']],
          ].map(([title, points]) => (
            <div key={title} className="border border-slate-200 rounded-xl p-3 bg-slate-50">
              <p className="font-semibold text-slate-800 mb-2">{title}</p>
              {points.map(p => <p key={p} className="text-slate-700 mb-0.5">• {p}</p>)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// PRODA export — NDIA PRODA-format bulk payment request CSV
function ProdaTab() {
  const showClaimErrors = useClaimErrors();
  const today = new Date();
  const monthAgo = new Date(today); monthAgo.setMonth(monthAgo.getMonth() - 1);
  const fmt = isoDate;
  const [range, setRange] = useState({ start: fmt(monthAgo), end: fmt(today) });
  const [status, setStatus] = useState('sent');
  const [clientId, setClientId] = useState('');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  useEffect(() => {
    api.get('/clients').then(r => setClients(r.data?.clients || [])).catch(() => {});
  }, []);

  const validateAndDownload = async () => {
    setValidating(true);
    setValidationResult(null);
    try {
      const p = { start_date: range.start, end_date: range.end, status };
      if (clientId) p.client_id = clientId;
      const r = await api.get(`/invoices/proda-validate?${new URLSearchParams(p)}`);
      if (!r.data.ready && r.data.issues?.length > 0) {
        setValidationResult(r.data);
        return; // show issues, let user decide
      }
    } catch { /* ignore validate errors, proceed */ }
    finally { setValidating(false); }
    await downloadProda();
  };

  const downloadProda = async () => {
    setLoading(true);
    try {
      const p = { start_date: range.start, end_date: range.end, status };
      if (clientId) p.client_id = clientId;
      const params = new URLSearchParams(p);
      const resp = await fetch(`/api/invoices/proda-export?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!resp.ok) {
        // A claim-validation refusal carries per-line detail; show that instead of a bare summary.
        const err = await readClaimRefusal(resp, showClaimErrors);
        if (err === true) return;
        toast.error(err.message || 'No NDIA line items found — make sure invoices have payment type set to NDIA');
        return;
      }
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `PRODA_BulkClaim_${range.start}_to_${range.end}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      setValidationResult(null);
      toast.success('PRODA file downloaded. Upload it to the NDIA PRODA portal to claim payment.');
    } catch { toast.error('Export failed'); }
    finally { setLoading(false); }
  };

  const lbl = 'text-[13px] text-slate-700 font-medium block mb-1';

  return (
    <div className="card p-5 sm:p-6 space-y-4">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center shrink-0">
          <Download size={18} className="text-slate-600" />
        </span>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-slate-900">NDIS PRODA bulk payment request</h3>
          <p className="text-sm text-slate-600 mt-1">
            A PRODA-format CSV of invoice line items. Upload it to the NDIA PRODA portal to claim payments in bulk.
            Your NDIS registration number and ABN must be set in <Link to="/settings/invoices" className="font-medium text-primary-700 hover:underline">invoice settings</Link>.
          </p>
        </div>
      </div>

      <Notice tone="slate" title="NDIA invoices only">
        The export includes only invoices whose payer is NDIA. Plan-managed and client-pay invoices are left out.
      </Notice>

      {/* Validation result panel */}
      {validationResult && validationResult.issues.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3" role="alert">
          <div className="flex items-center gap-2 text-red-800 font-semibold text-sm">
            <AlertCircle size={16} /> {plural(validationResult.issues.length, 'client')} {validationResult.issues.length === 1 ? 'is' : 'are'} missing PRODA details
          </div>
          <div className="space-y-1.5">
            {validationResult.issues.map(issue => (
              <div key={issue.clientId} className="flex items-center gap-2 flex-wrap text-xs text-red-800">
                <span className="font-semibold">{issue.clientName}</span>
                {issue.missingNdisNumber && <span className="px-1.5 py-0.5 bg-red-100 rounded">Missing NDIS number</span>}
                {issue.missingSupportItems > 0 && <span className="px-1.5 py-0.5 bg-red-100 rounded">{issue.missingSupportItems} lines missing a support item number</span>}
              </div>
            ))}
          </div>
          <p className="text-xs text-red-800">Fix these before exporting or the NDIA will reject the claim. You can still download now if you need to.</p>
          <div className="flex gap-2">
            <button type="button" onClick={downloadProda} disabled={loading} className="btn-secondary btn-sm !text-rose-700 !border-rose-300">
              Download anyway
            </button>
            <button type="button" onClick={() => setValidationResult(null)} className="btn-ghost btn-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 pt-3 border-t border-slate-100">
        <div>
          <label htmlFor="proda-from" className={lbl}>From</label>
          <input id="proda-from" type="date" value={range.start} onChange={e => setRange(r => ({ ...r, start: e.target.value }))} className="input-field" />
        </div>
        <div>
          <label htmlFor="proda-to" className={lbl}>To</label>
          <input id="proda-to" type="date" value={range.end} onChange={e => setRange(r => ({ ...r, end: e.target.value }))} className="input-field" />
        </div>
        <div>
          <label htmlFor="proda-status" className={lbl}>Invoice status</label>
          <select id="proda-status" className="input-field" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="sent,paid">Sent and paid</option>
          </select>
        </div>
        <div>
          <label htmlFor="proda-client" className={lbl}>Client</label>
          <select id="proda-client" className="input-field" value={clientId} onChange={e => setClientId(e.target.value)}>
            <option value="">All clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <button type="button" onClick={validateAndDownload} disabled={loading || validating} className="btn-primary w-full">
            {(loading || validating) ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            {validating ? 'Checking…' : loading ? 'Generating…' : 'Check and download'}
          </button>
        </div>
      </div>

      <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-700 space-y-1">
        <p className="font-semibold text-slate-800 mb-2">PRODA file columns</p>
        <p>RegistrationNumber · NDISNumber · SupportsDeliveredFrom · SupportsDeliveredTo · SupportNumber · ClaimReference · Quantity · Hours · UnitPrice · GSTCode · AuthorisedBy · ParticipantApproved · InKindFundingProgram · SupportPurpose · ABN of Support Provider</p>
      </div>
    </div>
  );
}

// Export every invoice line for a date range. Its own range, so the rest of the page never has two.
function ExportNdisDialog({ open, onClose, from, to, setFrom, setTo, onExport, busy }) {
  if (!open) return null;
  const now = new Date();
  const fyStart = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  const presets = [
    { label: 'This month', from: format(startOfMonth(now), 'yyyy-MM-dd'), to: format(now, 'yyyy-MM-dd') },
    { label: 'Last month', from: format(startOfMonth(subMonths(now, 1)), 'yyyy-MM-dd'), to: format(new Date(now.getFullYear(), now.getMonth(), 0), 'yyyy-MM-dd') },
    { label: 'This FY', from: `${fyStart}-07-01`, to: format(now, 'yyyy-MM-dd') },
    { label: 'Last FY', from: `${fyStart - 1}-07-01`, to: `${fyStart}-06-30` },
  ];
  return (
    <Dialog open onClose={onClose} size="sm" title="Export NDIS invoice lines"
      description="Every invoice line with the shift and timesheet it came from, as a CSV you can check."
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="button" onClick={onExport} disabled={busy || !from || !to} className="btn-primary">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Export CSV
          </button>
        </>
      )}>
      <div className="space-y-3">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Date presets">
          {presets.map(p => {
            const on = p.from === from && p.to === to;
            return (
              <button key={p.label} type="button" aria-pressed={on} onClick={() => { setFrom(p.from); setTo(p.to); }}
                className={clsx('min-h-[32px] px-3 rounded-full border text-[12px] font-medium',
                  on ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')}>
                {p.label}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="ndis-exp-from" className="block text-[13px] font-medium text-slate-700 mb-1">From</label>
            <input id="ndis-exp-from" type="date" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} className="input-field" />
          </div>
          <div>
            <label htmlFor="ndis-exp-to" className="block text-[13px] font-medium text-slate-700 mb-1">To</label>
            <input id="ndis-exp-to" type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} className="input-field" />
          </div>
        </div>
        <p className="text-xs text-slate-600">Lines whose billed units differ from their timesheet are flagged in the Variance column.</p>
      </div>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'generate',   label: 'Generate' },
  { id: 'sil',        label: 'SIL weekly' },
  { id: 'invoices',   label: 'Invoices' },
  { id: 'claims',     label: 'Claims' },
  { id: 'accounting', label: 'Accounting' },
  { id: 'xero',       label: 'Xero' },
  { id: 'quickbooks', label: 'QuickBooks' },
];

export default function Invoices() {
  const [tab, setTab]               = useUrlTab('tab', 'generate', TABS.map(t => t.id));
  // The Xero tab shows only when Xero is connected (Settings › Integrations).
  const connected = useConnectedIntegrations();
  const [invSettings, setInvSettings] = useState(INVOICE_DEFAULTS);

  useEffect(() => {
    api.get('/settings').then(r => {
      const s = r.data?.settings || {};
      const is = (s.invoice_settings && typeof s.invoice_settings === 'object') ? s.invoice_settings : {};
      // company_name: explicit invoice-settings name wins, else the org's company name (app_settings,
      // seeded from the organisation name) so the invoice "From" shows the real business, not a default.
      setInvSettings({ ...INVOICE_DEFAULTS, ...is, company_name: is.company_name || s.company_name || '' });
    }).catch(() => {});
  }, []);

  // The range the export actually covers, shown in its dialog so it is never a silent default.
  const [exportFrom, setExportFrom] = useState(format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'));
  const [exportTo, setExportTo]     = useState(format(new Date(), 'yyyy-MM-dd'));
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting]   = useState(false);

  // Export every invoice LINE with the shift and timesheet it came from, so the file can be
  // checked rather than just filed. It used to send no dates at all, which quietly gave you the
  // last 30 days under a button labelled "Export NDIS" — a financial-year export came out nearly
  // empty and nothing said why.
  // Per-line claim refusals, surfaced from any export button via ClaimErrCtx.
  const [claimErrors, setClaimErrors] = useState(null);

  const exportNdis = async () => {
    setExporting(true);
    try {
      const p = new URLSearchParams({ start_date: exportFrom, end_date: exportTo });
      const { data } = await api.get(`/invoices/export-ndis?${p}`);
      if (!data?.length) return toast.error(`No invoices between ${fmtDate(exportFrom)} and ${fmtDate(exportTo)}`);

      const COLS = [
        ['Invoice Number', 'invoice_number'], ['Invoice Status', 'invoice_status'],
        ['Pay To', 'payment_type'], ['NDIS Number', 'ndis_number'],
        ['Participant', 'participant_name'], ['Invoice Date', 'invoice_date'],
        ['Due Date', 'due_date'], ['Service Date', 'service_date'],
        ['Support Item', 'support_item_number'], ['Description', 'description'],
        ['Line Kind', 'line_kind'], ['Shift Date', 'shift_date'],
        ['Shift Start', 'shift_start'], ['Shift End', 'shift_end'],
        ['Shift Status', 'shift_status'], ['Support Worker', 'support_worker'],
        ['Timesheet Status', 'timesheet_status'], ['Timesheet Hours', 'timesheet_hours'],
        ['Billed Units', 'billed_units'], ['Rate', 'rate'], ['Line Total', 'line_total'],
        ['Variance', 'variance'], ['Invoice GST', 'gst_amount'], ['Invoice Total', 'invoice_total'],
      ];
      // Quote every cell AND escape the quotes inside it. The old version wrapped values in quotes
      // without escaping, so a name or description carrying a double quote shifted every column
      // after it and Excel read the wrong value into the wrong heading.
      const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const csv = [
        COLS.map(([h]) => cell(h)).join(','),
        ...data.map((r) => COLS.map(([, k]) => cell(r[k])).join(',')),
      ].join('\r\n');

      // BOM so Excel opens it as UTF-8 rather than mangling accented names.
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `ndis_invoice_lines_${exportFrom}_to_${exportTo}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);

      const flagged = data.filter((r) => r.variance).length;
      if (flagged) toast(`Exported ${data.length} lines. ${flagged} flagged in the Variance column.`, { icon: warnIcon });
      else toast.success(`Exported ${data.length} lines. All match their timesheets.`);
      setExportOpen(false);
    } catch (e) { toast.error(e.response?.data?.message || 'Export failed'); }
    finally { setExporting(false); }
  };

  return (
    <ClaimErrCtx.Provider value={setClaimErrors}>
    <InvSettingsCtx.Provider value={invSettings}>
      <ClaimErrorPanel data={claimErrors} onClose={() => setClaimErrors(null)} />
      <div className={PAGE_WIDE}>
        {/* The top bar already titles the page; the tabs name its screens. */}
        <Tabs ariaLabel="Invoices" value={tab} onChange={setTab}
          tabs={TABS.filter(t => (t.id !== 'xero' || connected.xero || tab === 'xero') && (t.id !== 'quickbooks' || connected.quickbooks || tab === 'quickbooks'))} />
        {tab === 'generate'   && <GenerateTab />}
        {tab === 'sil'        && <SilWeeklyTab />}
        {tab === 'invoices'   && <ListTab onGoGenerate={() => setTab('generate')} onExportNdis={() => setExportOpen(true)} />}
        {tab === 'claims'     && (
          <div className="space-y-4">
            <NdiaSubmitTab />
            <ProdaTab />
          </div>
        )}
        {tab === 'accounting' && <XeroTab />}
        {tab === 'xero'       && <XeroInvoicesTab />}
        {tab === 'quickbooks' && <QuickBooksInvoicesTab />}
      </div>
      <ExportNdisDialog open={exportOpen} onClose={() => setExportOpen(false)} busy={exporting}
        from={exportFrom} to={exportTo} setFrom={setExportFrom} setTo={setExportTo} onExport={exportNdis} />
    </InvSettingsCtx.Provider>
    </ClaimErrCtx.Provider>
  );
}
